import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SellerOnboardingAlert from "../components/SellerOnboardingAlert";
import VerificationRequiredModal from "../components/VerificationRequiredModal";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useAuth } from "../context/AuthContext";
import { isSuperAdmin } from "../utils/roles";
import type { DealAnalysisJobResult, DealAnalysisJobStatus } from "../features/deals/service";
import { dealsService } from "../features/deals/service";
import { apiClient } from "../lib/http/apiClient";
import { contactsService } from "../features/contacts/service";
import type { BusinessContactCreatePayload } from "../features/contacts/types";
import {
    CONTACT_VERTICAL_OPTIONS,
    CONTACT_ORG_TYPE_OPTIONS,
    hierarchyLevelSelectOptions,
} from "../features/contacts/constants";
import type { ParsedApiError } from "../lib/http/apiErrors";
import { parseApiError } from "../lib/http/apiErrors";
import { useBackgroundJobs } from "../lib/notifications/useBackgroundJobs";
import { fetchAnalysisJobStatus } from "../lib/notifications/activeJobsSync";
import { notificationRealtime } from "../lib/notifications/notificationRealtime";
import { useMyTier } from "../features/subscriptions/useMyTier";
import { getMonthlyDealLimit, hasReachedMonthlyDealLimit, countDealsThisMonth } from "../features/subscriptions/dealQuotaConfig";

function splitCommaList(value: string): string[] {
    return value.split(",").map((s) => s.trim()).filter(Boolean);
}

// ─────────────────────────────────────────────────────────────────────────────
// File viewer dialog — expand single file full-screen
// ─────────────────────────────────────────────────────────────────────────────
interface FileViewerDialogProps {
    file: File | null;
    previewUrl: string | null;
    onClose: () => void;
    onEdit?: () => void;
}
const FileViewerDialog: React.FC<FileViewerDialogProps> = ({ file, previewUrl, onClose, onEdit }) => {
    useBodyScrollLock(!!file);
    if (!file) return null;
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const isPdf   = file.type === "application/pdf";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
                    <p className="text-sm font-semibold text-gray-800 truncate max-w-xs">{file.name}</p>
                    <div className="flex items-center gap-2 shrink-0">
                        {onEdit && (
                            <button
                                onClick={() => { onEdit(); onClose(); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors"
                            >
                                ✏️ Replace
                            </button>
                        )}
                        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg text-xl leading-none font-bold transition-colors">✕</button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-50 p-4">
                    {isImage && previewUrl && <img src={previewUrl} alt={file.name} className="max-h-[75vh] max-w-full object-contain rounded-xl" />}
                    {isVideo && <video src={URL.createObjectURL(file)} controls className="max-h-[75vh] max-w-full rounded-xl" />}
                    {isPdf && <iframe src={URL.createObjectURL(file)} title={file.name} className="w-full h-[70vh] rounded-xl border border-gray-200" />}
                    {!isImage && !isVideo && !isPdf && (
                        <div className="flex flex-col items-center gap-3 py-16">
                            <span className="text-6xl">📄</span>
                            <p className="font-semibold text-gray-700">{file.name}</p>
                            <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB · {file.type || "document"}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Files carousel dialog — view all uploaded files with prev/next slider
// ─────────────────────────────────────────────────────────────────────────────
interface CarouselFile { file: File; previewUrl: string | null; label: string; }
interface FilesCarouselDialogProps {
    files: CarouselFile[];
    initialIndex?: number;
    onClose: () => void;
    onEditAt?: (index: number) => void;
}
const FilesCarouselDialog: React.FC<FilesCarouselDialogProps> = ({ files, initialIndex = 0, onClose, onEditAt }) => {
    const [idx, setIdx] = React.useState(initialIndex);
    useBodyScrollLock(files.length > 0);
    if (!files.length) return null;
    const cur = files[idx];
    const isImage = cur.file.type.startsWith("image/");
    const isVideo = cur.file.type.startsWith("video/");
    const isPdf   = cur.file.type === "application/pdf";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={onClose}>
            <div className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
                    <p className="text-sm font-semibold text-gray-800 truncate max-w-xs">{cur.label}</p>
                    <div className="flex items-center gap-2">
                        {onEditAt && (
                            <button
                                onClick={() => { onEditAt(idx); onClose(); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors"
                            >
                                ✏️ Replace
                            </button>
                        )}
                        <span className="text-xs text-gray-400">{idx + 1} / {files.length}</span>
                        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg text-xl leading-none font-bold transition-colors">✕</button>
                    </div>
                </div>
                {/* Content */}
                <div className="relative flex-1 flex items-center justify-center bg-gray-50 p-4 min-h-[50vh]">
                    {/* Prev */}
                    {files.length > 1 && (
                        <button
                            onClick={() => setIdx((i) => (i - 1 + files.length) % files.length)}
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full shadow-md flex items-center justify-center text-gray-700 hover:bg-white z-10 border border-gray-200"
                        >‹</button>
                    )}
                    {isImage && cur.previewUrl && <img src={cur.previewUrl} alt={cur.label} className="max-h-[65vh] max-w-full object-contain rounded-xl" />}
                    {isVideo && <video src={URL.createObjectURL(cur.file)} controls className="max-h-[65vh] max-w-full rounded-xl" />}
                    {isPdf && <iframe src={URL.createObjectURL(cur.file)} title={cur.label} className="w-full h-[60vh] rounded-xl border border-gray-200" />}
                    {!isImage && !isVideo && !isPdf && (
                        <div className="flex flex-col items-center gap-3 py-12">
                            <span className="text-6xl">📄</span>
                            <p className="font-semibold text-gray-700">{cur.file.name}</p>
                            <p className="text-xs text-gray-400">{(cur.file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                    )}
                    {/* Next */}
                    {files.length > 1 && (
                        <button
                            onClick={() => setIdx((i) => (i + 1) % files.length)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full shadow-md flex items-center justify-center text-gray-700 hover:bg-white z-10 border border-gray-200"
                        >›</button>
                    )}
                </div>
                {/* Dot indicators */}
                {files.length > 1 && (
                    <div className="flex justify-center gap-1.5 py-3 border-t border-gray-100 bg-white shrink-0">
                        {files.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-blue-500 w-4" : "bg-gray-300"}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
type Step = "home" | "uploading" | "analyzing" | "done" | "error";

type DealMode =
    | "DEAL"           // Generic deal / flyer upload
    | "AI_CONTACT"     // Contact card
    | "AI_WEBINAR"     // Webinar event
    | "AI_SHOWCASE";   // Curated showcase

interface AiResult extends DealAnalysisJobResult {
    category?: string;
    category_group?: string;
    item_type?: string;
    deal_type?: string;
    destinations?: string[];
    duration?: string;
    inclusions?: string[];
    exclusions?: string[];
    highlights?: string[];
    tags?: string[];
    original_price?: number;
    discounted_price?: number;
    star_rating?: number;
    ai_status?: string;
    ai_message?: string;
    ai_suggested_title?: string;
    ai_suggested_description?: string;
    ai_used_suggested_title?: boolean;
    ai_used_suggested_description?: boolean;
    categories?: { primary_slug?: string; secondary_slugs?: string[] };
    detected_group?: string;
    seller_types_label?: string;
    ai_token_usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
    ai_model?: string;
    ai_elapsed_ms?: number;
}

const MODE_ICONS: Record<DealMode, string> = {
    DEAL: "🖼️",
    AI_CONTACT: "👤",
    AI_WEBINAR: "📹",
    AI_SHOWCASE: "✨",
};

// ─────────────────────────────────────────────────────────────────────────────
// File helpers
// ─────────────────────────────────────────────────────────────────────────────
function supportsFlyerAnalysis(mode: DealMode, file: File | null): boolean {
    if (!file || mode === "AI_CONTACT") return false;
    // AI analysis now supports videos (backend extracts frames for analysis)
    return (
        file.type.startsWith("image/") ||
        file.type.startsWith("video/") ||
        file.type === "application/pdf" ||
        file.type === "application/msword" ||
        file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    );
}

function acceptedFiles(mode: DealMode): string {
    if (mode === "AI_CONTACT") return "";
    return "image/jpeg,image/png,image/webp,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,video/mp4,video/quicktime,video/webm";
}

function getMaxUploadMb(_mode: DealMode, fileType?: string): number {
    if (fileType?.startsWith("video/")) {
        return Number(import.meta.env.VITE_MAX_VIDEO_UPLOAD_MB ?? 100);
    }
    return Number(import.meta.env.VITE_MAX_FILE_UPLOAD_MB ?? 50);
}

// Maximum number of gallery files (images/videos/PDF pages) attached to a single deal.
// Mirrors backend MAX_DEAL_GALLERY_FILES — override via VITE_MAX_GALLERY_FILES.
const MAX_GALLERY_FILES = Number(import.meta.env.VITE_MAX_GALLERY_FILES ?? 10);

// Mirrors backend GALLERY_ATTACHMENT_ALLOWED_CONTENT_TYPES (backend/app/core/config.py).
const GALLERY_ACCEPTED_TYPES = [
    "image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/csv", "text/plain",
];
const GALLERY_ACCEPT_ATTR = GALLERY_ACCEPTED_TYPES.join(",");

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
// localStorage key for persisting in-progress analysis across navigation AND page refresh
const ANALYSIS_PERSIST_KEY = "create_deal_analysis_v1";
type AnalysisPersisted = { dealId: string; dealTitle: string; jobId: string; startedAt: number };

function saveAnalysisState(data: AnalysisPersisted) {
    try { localStorage.setItem(ANALYSIS_PERSIST_KEY, JSON.stringify(data)); } catch { /* quota */ }
}
function loadAnalysisState(): AnalysisPersisted | null {
    try {
        const raw = localStorage.getItem(ANALYSIS_PERSIST_KEY);
        if (!raw) return null;
        const d = JSON.parse(raw) as AnalysisPersisted;
        // Discard if older than 2 hours
        if (Date.now() - d.startedAt > 2 * 60 * 60 * 1000) {
            localStorage.removeItem(ANALYSIS_PERSIST_KEY);
            return null;
        }
        return d;
    } catch { return null; }
}
function clearAnalysisState() {
    try { localStorage.removeItem(ANALYSIS_PERSIST_KEY); } catch { /* ignore */ }
}

const CreateDealPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const myTier = useMyTier();
    const isDevUser = isSuperAdmin(user?.role);
    const { trackDealAnalysis, jobs: bgJobs } = useBackgroundJobs();

    // ── State ────────────────────────────────────────────────────────────────
    const [mode, setMode] = useState<DealMode>("DEAL");
    const [title, setTitle] = useState(() => loadAnalysisState()?.dealTitle ?? "");
    const [description, setDescription] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);

    // AI_CONTACT → POST /api/contacts (BusinessContactCreate)
    const [contactName, setContactName] = useState("");
    const [contactDesignation, setContactDesignation] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactVertical, setContactVertical] = useState("");
    const [contactCompany, setContactCompany] = useState("");
    const [contactCompanyCode, setContactCompanyCode] = useState("");
    const [contactOrgType, setContactOrgType] = useState("");
    const [contactDepartment, setContactDepartment] = useState("");
    const [contactSubDepartment, setContactSubDepartment] = useState("");
    const [contactHierarchyLevel, setContactHierarchyLevel] = useState("");
    const [contactRegion, setContactRegion] = useState("");
    const [contactCoverage, setContactCoverage] = useState("");
    const [contactCity, setContactCity] = useState("");
    const [contactCountry, setContactCountry] = useState("");
    const [contactSpecialties, setContactSpecialties] = useState("");
    const [contactNotes, setContactNotes] = useState("");

    // AI_WEBINAR fields
    const [webinarDate, setWebinarDate] = useState("");
    const [webinarTime, setWebinarTime] = useState("");
    const [webinarLink, setWebinarLink] = useState("");
    const [presenter, setPresenter] = useState("");



    // Gallery (extra images selected before/after submit)
    const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
    const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);
    const [galleryUploading, setGalleryUploading] = useState(false);
    const [galleryProgress, setGalleryProgress] = useState<string | null>(null);
    const [galleryUploadedKeys, setGalleryUploadedKeys] = useState<string[]>([]);

    // Workflow
    // Initialise directly from localStorage so there's no flash of the home form on refresh
    const [step, setStep] = useState<Step>(() => loadAnalysisState() ? "analyzing" : "home");
    const [errorMsg, setErrorMsg] = useState("");
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [errorInfo, setErrorInfo] = useState<ParsedApiError | null>(null);
    const [createdDealId, setCreatedDealId] = useState<string | null>(() => loadAnalysisState()?.dealId ?? null);
    const [createdDealCollection, setCreatedDealCollection] = useState<string | null>(null);
    const [createdContactId, setCreatedContactId] = useState<string | null>(null);
    const [aiResult, setAiResult] = useState<AiResult | null>(null);
    const [isApplyingAiField, setIsApplyingAiField] = useState<"title" | "description" | null>(null);
    const [analysisJob, setAnalysisJob] = useState<DealAnalysisJobStatus | null>(null);

    // Slug editor state
    const [primarySlug, setPrimarySlug] = useState("");
    const [primaryGroupSlug, setPrimaryGroupSlug] = useState(""); // deal type / collection group
    const [secondarySlugs, setSecondarySlugs] = useState<string[]>([]);
    const [isSavingSlugs, setIsSavingSlugs] = useState(false);
    const [slugSaved, setSlugSaved] = useState(false);
    const [categoryOptions, setCategoryOptions] = useState<{ slug: string; name: string; group_slug: string; is_promotional: boolean }[]>([]);
    const [categoryLoadError, setCategoryLoadError] = useState<string | null>(null);

    useEffect(() => {
        apiClient.get("/api/v2/categories")
            .then((r) => r.data as unknown)
            .then((data: unknown) => {
                if (Array.isArray(data)) {
                    if (data.length === 0) setCategoryLoadError("No categories configured in this environment.");
                    else setCategoryOptions(data as typeof categoryOptions);
                } else {
                    setCategoryLoadError("Unexpected response from categories API.");
                }
            })
            .catch((e: unknown) => setCategoryLoadError(e instanceof Error ? e.message : "Failed to load categories."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Deal package toggle + package-specific slugs
    const [isInPackages, setIsInPackages] = useState(false);
    const [isTogglingPackages, setIsTogglingPackages] = useState(false);
    const [packageToggleSaved, setPackageToggleSaved] = useState(false);
    const [pkgPrimarySlug, setPkgPrimarySlug] = useState("");
    const [pkgSecondarySlugs, setPkgSecondarySlugs] = useState<string[]>([]);

    // File expand / carousel dialog state
    const [expandFile, setExpandFile] = useState<{ file: File; previewUrl: string | null } | null>(null);
    const [showCarousel, setShowCarousel] = useState<{ initialIndex: number } | null>(null);
    const [showRawExtractionDialog, setShowRawExtractionDialog] = useState(false);
    useBodyScrollLock(!!expandFile || !!showCarousel || showRawExtractionDialog);
    const [analysisElapsed, setAnalysisElapsed] = useState(0); // seconds since analysis started
    // Bumped whenever persisted analysis state is cleared, to force a re-render so
    // the banner + form-block (which read loadAnalysisState() during render) update.
    const [, setAnalysisClearedTick] = useState(0);
    // Friendly notice shown when a previous stuck upload was auto-reset by the
    // backend (stale reaping), so the user knows why the block disappeared.
    const [staleNotice, setStaleNotice] = useState<string | null>(null);

    // ── Single, idempotent terminal transition for a finished analysis job ────
    // Completion can be observed by three independent paths: the inline submit
    // flow's poll, the resume-on-mount poll, and the live WS handler. Whichever
    // sees the terminal status first moves the UI to the result/error screen.
    // Guarded by deal id so the other paths don't re-run resolveCollection or
    // re-transition — this is what lets the WS update finish the analysis
    // without requiring a manual page reload.
    const finalizedDealRef = useRef<string | null>(null);
    const finalizeAnalysis = useCallback(async (finalJob: DealAnalysisJobStatus, dealId: string) => {
        if (!dealId || finalizedDealRef.current === dealId) return;
        finalizedDealRef.current = dealId;

        if (finalJob.status === "failed") {
            const errorCode = finalJob.error_code ?? undefined;
            const jobErrMsg = finalJob.user_message || finalJob.error || "AI analysis failed.";
            const isMismatch = errorCode === "seller_type_mismatch";
            setAiResult({
                ai_status: isMismatch ? "seller_type_mismatch" : "failed",
                ai_message: jobErrMsg,
                ...(isMismatch && finalJob.error_detail ? {
                    detected_group: finalJob.error_detail.detected_group ?? undefined,
                    seller_types_label: finalJob.error_detail.seller_types ?? undefined,
                } : {}),
            });
            clearAnalysisState();
            // seller_type_mismatch has its own full-screen render on the done step;
            // any other failure shows the error screen with the server message.
            if (isMismatch) {
                setStep("done");
            } else {
                setErrorMsg(jobErrMsg);
                setErrorInfo(null);
                setStep("error");
                if (finalJob.staging_deal_deleted) {
                    setCreatedDealId(null);
                }
            }
            return;
        }

        const extracted = (finalJob.result as AiResult | null) ?? { ai_status: "done", ai_message: "AI analysis completed." };

        // The job finished, but AI may still have produced nothing usable (e.g. the
        // LLM provider errored mid-run → ai_status "no_data"/"failed"/"error").
        // Treat these as a failed analysis so the UI shows a proper error instead of
        // the "Deal Added" success screen. The listing itself is still saved.
        const analysisFailedStatuses = ["failed", "no_data", "error"];
        if (extracted.ai_status && analysisFailedStatuses.includes(extracted.ai_status)) {
            setAiResult(extracted);
            clearAnalysisState();
            setErrorMsg(
                extracted.ai_message ||
                "AI analysis could not extract details from your upload. Your listing was saved — you can add details manually via Edit.",
            );
            setErrorInfo(null);
            setStep("error");
            return;
        }

        setAiResult(extracted);
        // AI may have reclassified the deal into a different collection — resolve the actual one.
        try {
            const resolved = await dealsService.resolveCollection(dealId);
            if (resolved) {
                setCreatedDealCollection(resolved.collection);
                setCreatedDealId(resolved.typedId);
            }
        } catch { /* non-fatal — collection resolution is best-effort */ }
        if (isDevUser && extracted) {
            setShowRawExtractionDialog(true);
        }
        clearAnalysisState();
        setStep("done");
    }, [isDevUser]);

    // ── Restore in-progress analysis on mount (survives refresh + navigation) ──
    useEffect(() => {
        if (step !== "home") return;
        const persisted = loadAnalysisState();
        if (!persisted) return;

        // Check bgJobs if already loaded — use as source of truth when available
        const job = bgJobs.get(`deal:${persisted.dealId}`);
        if (job && (job.status === "completed" || job.status === "failed")) {
            clearAnalysisState();
            return;
        }

        // Restore immediately — bgJobs may still be loading (WebSocket reconnecting after refresh).
        // The bgJobs effect below will update analysisJob once jobs arrive.
        setCreatedDealId(persisted.dealId);
        setTitle(persisted.dealTitle);
        setAnalysisJob({
            job_id: persisted.jobId,
            deal_id: persisted.dealId,
            status: (job?.status ?? "running") as DealAnalysisJobStatus["status"],
            stage: job?.stage ?? "queued",
        } as DealAnalysisJobStatus);
        setStep("analyzing");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run once on mount only

    // ── When bgJobs loads after a refresh, validate the restored analysis ──────
    useEffect(() => {
        if (step !== "analyzing" || !createdDealId || bgJobs.size === 0) return;
        const job = bgJobs.get(`deal:${createdDealId}`);
        if (!job) return; // job not in map yet — keep showing "analyzing"
        if (job.status === "completed" || job.status === "failed") {
            clearAnalysisState();
            // Don't reset step here — the existing bgJobs effect handles the transition
        }
    }, [bgJobs, step, createdDealId]);

    // ── Save analysis state to sessionStorage when it starts ─────────────────
    // (also done inline at the call site, but this keeps WS updates reflected)
    useEffect(() => {
        if (step !== "analyzing" || !createdDealId) return;
        const job = bgJobs.get(`deal:${createdDealId}`);
        if (job && job.status !== "completed" && job.status !== "failed") {
            setAnalysisJob((prev) => ({
                ...prev,
                job_id: prev?.job_id ?? job.jobId ?? "",
                deal_id: createdDealId,
                status: job.status as DealAnalysisJobStatus["status"],
                stage: job.stage,
            } as DealAnalysisJobStatus));
        } else if (job?.status === "completed" || job?.status === "failed") {
            clearAnalysisState();
        }
    }, [bgJobs, step, createdDealId]);

    // ── Clear persisted analysis once the SERVER reports its job terminal ─────
    // Runs on ANY step (the effects above only fire while step==="analyzing").
    // The server is the sole authority on completion/staleness; this effect only
    // reflects what syncFromServer / WS already resolved, releasing the home-step
    // "analysis in progress" banner + form block. No client-side timing logic.
    useEffect(() => {
        const persisted = loadAnalysisState();
        if (!persisted) return;
        const job = bgJobs.get(`deal:${persisted.dealId}`);
        if (job && (job.status === "completed" || job.status === "failed")) {
            clearAnalysisState();
            setAnalysisClearedTick((n) => n + 1); // force banner + form-block to re-evaluate
            // A stuck upload that the server auto-reset comes back failed with a
            // server-provided message — surface it so the released block isn't confusing.
            if (job.status === "failed" && step === "home" && job.errorMessage) {
                setStaleNotice(job.errorMessage);
            }
        }
    }, [bgJobs, step]);

    // ── Fallback: ask the server directly if bgJobs never picks up this job ──
    // If the job finished (and aged out of /jobs/active) before this tab ever
    // tracked it — e.g. a fresh reload long after analysis completed — bgJobs
    // will never contain a `deal:{dealId}` entry, so the effect above never
    // fires and the "AI analysis in progress" banner would be stuck forever.
    // Poll the job directly by id as a last resort.
    useEffect(() => {
        const persisted = loadAnalysisState();
        if (!persisted?.jobId) return;
        if (bgJobs.has(`deal:${persisted.dealId}`)) return; // handled by the effect above

        let cancelled = false;
        const check = async () => {
            const real = await fetchAnalysisJobStatus(persisted.jobId);
            if (cancelled || !real) return;
            if (real.status === "completed" || real.status === "failed") {
                clearAnalysisState();
                setAnalysisClearedTick((n) => n + 1);
            }
        };
        check();
        const id = window.setInterval(check, 5_000);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [bgJobs, step]);

    // ── Resume polling when page mounts mid-analysis (refresh / navigation) ──
    const resumedPollRef = useRef(false);
    useEffect(() => {
        if (resumedPollRef.current) return;
        const persisted = loadAnalysisState();
        if (!persisted?.jobId || step !== "analyzing") return;
        resumedPollRef.current = true;
        // Seed bgJobs immediately so the BG bar shows the in-progress job
        trackDealAnalysis(persisted.dealId, persisted.jobId, persisted.dealTitle);
        pollAnalysisJob(persisted.jobId).then((finalJob) => {
            // Shared terminal transition (the WS handler may have already run it).
            finalizeAnalysis(finalJob, persisted.dealId);
        }).catch(() => {
            clearAnalysisState();
            setStep("home");
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run once on mount only

    // ── Elapsed timer — counts up while uploading or analyzing ───────────────
    // Imported via top-level: useCallback, useState already imported; useEffect needed
    useEffect(() => {
        if (step !== "uploading" && step !== "analyzing") {
            setAnalysisElapsed(0);
            return;
        }
        setAnalysisElapsed(0);
        const interval = window.setInterval(() => setAnalysisElapsed((s) => s + 1), 1000);
        return () => window.clearInterval(interval);
    }, [step]);

    // ── Live WS pipeline updates during deal analysis ────────────────────────
    useEffect(() => {
        if (step !== "analyzing" || !createdDealId) return;
        return notificationRealtime.subscribe((msg) => {
            if (msg.type !== "deal:analysis_progress") return;
            const m = msg as { type: string; stage?: string; deal_id?: string; job_id?: string; message?: string };
            if (m.deal_id && m.deal_id !== createdDealId) return;
            const stage = m.stage ?? "";
            if (!stage) return;
            const statusMap: Record<string, DealAnalysisJobStatus["status"]> = {
                queued: "queued",
                ocr_running: "ocr_running",
                ocr_completed: "ocr_completed",
                llm_running: "llm_running",
                llm_completed: "llm_completed",
                index_running: "index_running",
                index_completed: "index_completed",
                completed: "completed",
                failed: "failed",
            };
            const mapped = statusMap[stage];
            if (!mapped) return;
            setAnalysisJob((prev) => ({
                ...prev,
                job_id: m.job_id ?? prev?.job_id ?? "",
                deal_id: m.deal_id ?? createdDealId,
                status: mapped,
                stage,
            } as DealAnalysisJobStatus));

            // Terminal stage over the WS: the message only carries the stage, not
            // the extracted result, so fetch the full job and finalize. finalizeAnalysis
            // is guarded by deal id, so if the inline/resume poll already finished
            // this is a no-op — but if that poll promise was lost (navigation, WS
            // reconnect, started in another tab) this is what completes the UI
            // instead of leaving the spinner stuck until a manual reload.
            if (mapped === "completed" || mapped === "failed") {
                const jobId = m.job_id ?? loadAnalysisState()?.jobId ?? "";
                if (jobId) {
                    dealsService
                        .getAnalysisJob(jobId)
                        .then((finalJob) => finalizeAnalysis(finalJob, createdDealId))
                        .catch(() => {});
                }
            }
        });
    }, [step, createdDealId, finalizeAnalysis]);

    // ── File handling ─────────────────────────────────────────────────────────
    const handleFile = useCallback(
        (f: File) => {
            const isVideo = f.type.startsWith("video/");
            const maxMb = getMaxUploadMb(mode, f.type);
            if (f.size > maxMb * 1024 * 1024) {
                const sizeMb = (f.size / 1024 / 1024).toFixed(1);
                setErrorMsg(
                    isVideo
                        ? `Video too large (${sizeMb} MB). Maximum video size is ${maxMb} MB.`
                        : `File too large (${sizeMb} MB). Maximum size for images and documents is ${maxMb} MB.`
                );
                setErrorInfo(null);
                return;
            }
            const acceptedMime = acceptedFiles(mode).split(",");
            if (f.type && acceptedMime.length && !acceptedMime.includes(f.type)) {
                setErrorMsg(`Unsupported file type (${f.type || f.name}). Accepted: image, PDF, DOC, or video.`);
                setErrorInfo(null);
                return;
            }
            setErrorMsg("");
            setErrorInfo(null);
            setFile(f);
            if (f.type.startsWith("image/")) {
                setPreview(URL.createObjectURL(f));
            } else {
                setPreview(null);
            }
        },
        [mode]
    );

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) handleFile(e.target.files[0]);
    };

    const onDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
        },
        [handleFile]
    );

    const onGalleryFilesSelected = (files: FileList | null) => {
        if (!files) return;
        const remainingSlots = MAX_GALLERY_FILES - galleryFiles.length;
        if (remainingSlots <= 0) {
            setErrorMsg(`Maximum ${MAX_GALLERY_FILES} gallery images/videos allowed per deal.`);
            setErrorInfo(null);
            return;
        }
        const valid: File[] = [];
        let rejectedForLimit = false;
        for (const f of Array.from(files)) {
            if (!GALLERY_ACCEPTED_TYPES.includes(f.type)) continue;
            const maxMb = getMaxUploadMb(mode, f.type);
            if (f.size > maxMb * 1024 * 1024) {
                const sizeMb = (f.size / 1024 / 1024).toFixed(1);
                setErrorMsg(
                    f.type.startsWith("video/")
                        ? `Video too large (${sizeMb} MB). Maximum video size is ${maxMb} MB.`
                        : `File too large (${sizeMb} MB). Maximum size for images is ${maxMb} MB.`
                );
                setErrorInfo(null);
                return;
            }
            if (valid.length >= remainingSlots) {
                rejectedForLimit = true;
                break;
            }
            valid.push(f);
        }
        if (rejectedForLimit) {
            setErrorMsg(`Maximum ${MAX_GALLERY_FILES} gallery images/videos allowed per deal. Only the first ${remainingSlots} of your selection were added.`);
            setErrorInfo(null);
        }
        setGalleryFiles((prev) => [...prev, ...valid]);
        setGalleryPreviews((prev) => [
            ...prev,
            ...valid.map((f) => (f.type.startsWith("image/") ? URL.createObjectURL(f) : "")),
        ]);
    };

    const removeGalleryFile = (idx: number) => {
        setGalleryFiles((prev) => prev.filter((_, i) => i !== idx));
        setGalleryPreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    const uploadGalleryToExistingDeal = async (dealId: string) => {
        if (!galleryFiles.length) return;
        setGalleryUploading(true);
        const uploaded: string[] = [];
        let lastFailure: ParsedApiError | null = null;
        for (let i = 0; i < galleryFiles.length; i++) {
            setGalleryProgress(`Uploading gallery ${i + 1}/${galleryFiles.length}…`);
            try {
                const results = await dealsService.uploadGalleryFiles(dealId, [galleryFiles[i]]);
                if (results[0]?.key) uploaded.push(results[0].key);
            } catch (err) {
                // Don't block deal creation on a gallery failure, but surface it
                // (e.g. the server-side max-images-per-deal cap) instead of
                // silently dropping the file.
                lastFailure = parseApiError(err, "Failed to upload one or more gallery files.");
            }
        }
        setGalleryUploadedKeys((prev) => [...prev, ...uploaded]);
        setGalleryFiles([]);
        setGalleryPreviews([]);
        setGalleryUploading(false);
        setGalleryProgress(null);
        if (lastFailure) {
            setErrorMsg(lastFailure.message);
            setErrorInfo(lastFailure);
        }
    };

    // ── Build description from mode-specific fields ─────────────────────────
    const buildDescription = (): string => {
        if (mode === "AI_WEBINAR") {
            return [presenter && `Presenter: ${presenter}`, webinarDate && `Date: ${webinarDate}`, webinarTime && `Time: ${webinarTime}`, webinarLink && `Link: ${webinarLink}`, description].filter(Boolean).join(" | ");
        }
        return description;
    };

    // ── Build title ─────────────────────────────────────────────────────────
    const buildTitle = (): string => {
        if (mode === "AI_WEBINAR" && !title) return "Webinar Event";
        if (mode === "AI_SHOWCASE" && !title) return "Seller Showcase";
        return title;
    };

    // ── Validate ─────────────────────────────────────────────────────────────
    const validateForm = (): boolean => {
        if (mode === "AI_CONTACT") {
            if (!contactName.trim()) {
                setErrorMsg("Please enter full name.");
                setErrorInfo(null);
                return false;
            }
            if (!contactVertical.trim()) {
                setErrorMsg("Please select a vertical.");
                setErrorInfo(null);
                return false;
            }
            if (!contactCompany.trim()) {
                setErrorMsg("Please enter company / organization.");
                setErrorInfo(null);
                return false;
            }
            if (!contactOrgType.trim()) {
                setErrorMsg("Please select organization type.");
                setErrorInfo(null);
                return false;
            }
            if (!title.trim()) {
                setErrorMsg("Please enter a card title.");
                setErrorInfo(null);
                return false;
            }
            if (!description.trim()) {
                setErrorMsg("Please enter a description.");
                setErrorInfo(null);
                return false;
            }
        } else {
            if (!title.trim()) {
                setErrorMsg("Please enter a title for your deal.");
                setErrorInfo(null);
                return false;
            }
            if (!description.trim()) {
                setErrorMsg("Please enter a description for your deal.");
                setErrorInfo(null);
                return false;
            }
        }
        return true;
    };

    // ── Seed slug editor when AI result arrives ───────────────────────────────
    const ITEM_TYPE_TO_GROUP: Record<string, string> = {
        PACKAGE: "packages", STAY: "stays", EXPERIENCE: "experiences",
        SERVICE: "services", FLIGHT: "flights", TRANSFER: "transfers", CRUISE: "cruises",
    };
    useEffect(() => {
        if (!aiResult?.categories) return;
        const cats = aiResult.categories as Record<string, unknown>;
        const aiRaw = aiResult as unknown as Record<string, unknown>;
        if (cats.primary_slug && !primarySlug) setPrimarySlug(cats.primary_slug as string);
        if (!primaryGroupSlug) {
            // Prefer explicit group_slug, fall back to item_type mapping
            const g = (cats.group_slug as string)
                || ITEM_TYPE_TO_GROUP[(aiRaw.item_type as string) || ""]
                || "";
            if (g) setPrimaryGroupSlug(g);
        }
        if (Array.isArray(cats.secondary_slugs) && cats.secondary_slugs.length && !secondarySlugs.length)
            setSecondarySlugs(cats.secondary_slugs as string[]);
        if (cats.package_primary_slug && !pkgPrimarySlug) setPkgPrimarySlug(cats.package_primary_slug as string);
        if (Array.isArray(cats.package_secondary_slugs) && cats.package_secondary_slugs.length && !pkgSecondarySlugs.length)
            setPkgSecondarySlugs(cats.package_secondary_slugs as string[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiResult]);

    // ── Seed slug editor + package toggle from the resolved deal ─────────────
    useEffect(() => {
        if (!createdDealCollection || !createdDealId) return;
        dealsService.getDeal(createdDealCollection, createdDealId).then((deal) => {
            // `categories` lives only in `ai_extracted_raw` on the single-deal
            // response — the top-level key is stripped server-side.
            const aiRaw = (deal as unknown as Record<string, unknown>).ai_extracted_raw as Record<string, unknown> | undefined;
            const cats = aiRaw?.categories as Record<string, unknown> | undefined;
            if (cats?.primary_slug && !primarySlug) setPrimarySlug(cats.primary_slug as string);
            if (cats?.group_slug && !primaryGroupSlug) setPrimaryGroupSlug(cats.group_slug as string);
            if (Array.isArray(cats?.secondary_slugs) && (cats.secondary_slugs as string[]).length && !secondarySlugs.length)
                setSecondarySlugs(cats.secondary_slugs as string[]);
            if (cats?.package_primary_slug && !pkgPrimarySlug) setPkgPrimarySlug(cats.package_primary_slug as string);
            if (Array.isArray(cats?.package_secondary_slugs) && (cats.package_secondary_slugs as string[]).length && !pkgSecondarySlugs.length)
                setPkgSecondarySlugs(cats.package_secondary_slugs as string[]);
            if ((deal as Record<string, unknown>).is_in_packages) setIsInPackages(true);
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdDealCollection, createdDealId]);

    const DEAL_TYPE_OPTIONS = [
        { value: "stays",       label: "Stays" },
        { value: "experiences", label: "Experiences" },
        { value: "services",    label: "Services" },
        { value: "flights",     label: "Flights" },
        { value: "transfers",   label: "Transfers" },
        { value: "cruises",     label: "Cruises" },
        { value: "packages",    label: "Packages" },
    ] as const;

    const saveSlugs = async () => {
        if (!createdDealId || !createdDealCollection) return;
        setIsSavingSlugs(true);
        setSlugSaved(false);
        try {
            const origGroup = (aiResult?.categories as Record<string, unknown> | undefined)?.group_slug as string | undefined;
            const groupChanged = primaryGroupSlug && primaryGroupSlug !== origGroup;
            const payload: Record<string, unknown> = {
                categories: {
                    ...(aiResult?.categories ?? {}),
                    primary_slug: primarySlug.trim(),
                    group_slug: primaryGroupSlug,
                    secondary_slugs: secondarySlugs.filter(Boolean),
                    package_primary_slug: pkgPrimarySlug.trim() || undefined,
                    package_secondary_slugs: pkgSecondarySlugs.filter(Boolean),
                    ai_primary_slug: (aiResult?.categories as Record<string, unknown> | undefined)?.primary_slug,
                    ai_secondary_slugs: (aiResult?.categories as Record<string, unknown> | undefined)?.secondary_slugs,
                },
                category_slug: primarySlug.trim(),
            };
            if (groupChanged) payload.retype_to_group = primaryGroupSlug;
            await dealsService.updateInventoryItem(createdDealCollection, createdDealId, payload);
            setSlugSaved(true);
            setTimeout(() => setSlugSaved(false), 3000);
        } catch (err) {
            const parsed = parseApiError(err, "Failed to save slugs.");
            setErrorMsg(parsed.message);
            setErrorInfo(parsed);
        } finally {
            setIsSavingSlugs(false);
        }
    };

    const togglePackages = async (next: boolean) => {
        if (!createdDealId || !createdDealCollection) return;
        setIsTogglingPackages(true);
        setPackageToggleSaved(false);
        try {
            await dealsService.updateInventoryItem(createdDealCollection, createdDealId, { is_in_packages: next });
            setIsInPackages(next);
            setPackageToggleSaved(true);
            setTimeout(() => setPackageToggleSaved(false), 3000);
        } catch (err) {
            const parsed = parseApiError(err, "Failed to update packages setting.");
            setErrorMsg(parsed.message);
            setErrorInfo(parsed);
        } finally {
            setIsTogglingPackages(false);
        }
    };

    const pollAnalysisJob = useCallback(async (jobId: string): Promise<DealAnalysisJobStatus> => {
        // Adaptive polling: fast early (3s), slow after 90s (6s). Max total ~10 minutes.
        // Image cleaning alone can take 3–5 minutes (3 model attempts + PIL fallback).
        const MAX_ATTEMPTS = 120;
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
            const status = await dealsService.getAnalysisJob(jobId);
            setAnalysisJob(status);
            if (status.status === "completed" || status.status === "failed") {
                return status;
            }
            // Fast poll first 30 attempts (≈90s), then slow poll
            const delay = attempt < 30 ? 3000 : 6000;
            await new Promise((resolve) => window.setTimeout(resolve, delay));
        }
        // Final check before giving up — job may have just completed
        const final = await dealsService.getAnalysisJob(jobId);
        setAnalysisJob(final);
        if (final.status === "completed" || final.status === "failed") {
            return final;
        }
        throw new Error("AI analysis is taking longer than expected. Please check My Inventory — your listing may have been saved.");
    }, []);

    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg("");
        setErrorInfo(null);
        if (!validateForm()) return;

        // ── AI Contact → business_contacts (not deals) ─────────────────────
        if (mode === "AI_CONTACT") {
            setStep("uploading");
            setAnalysisJob(null);
            setCreatedDealId(null);
            setCreatedDealCollection(null);
            setCreatedContactId(null);
            setAiResult(null);
            try {
                const phone = contactPhone.trim();
                const email = contactEmail.trim();
                const desig = contactDesignation.trim();
                const hlRaw = contactHierarchyLevel.trim();
                const hierarchyParsed = hlRaw === "" ? NaN : parseInt(hlRaw, 10);
                const payload: BusinessContactCreatePayload = {
                    title: title.trim(),
                    description: description.trim(),
                    contact_name: contactName.trim(),
                    designation: desig || undefined,
                    roles: desig ? [desig] : [],
                    phone_numbers: phone ? [phone] : [],
                    whatsapp_numbers: phone ? [phone] : [],
                    emails: email ? [email] : [],
                    organization_name: contactCompany.trim(),
                    organization_type: contactOrgType.trim(),
                    department: contactDepartment.trim() || undefined,
                    sub_department: contactSubDepartment.trim() || undefined,
                    vertical: contactVertical.trim(),
                    company_code: contactCompanyCode.trim() || undefined,
                    region: contactRegion.trim() || undefined,
                    coverage_areas: splitCommaList(contactCoverage),
                    hierarchy_level:
                        hlRaw !== "" && !Number.isNaN(hierarchyParsed) ? hierarchyParsed : undefined,
                    tags: [],
                    city: contactCity.trim() || undefined,
                    country: contactCountry.trim() || undefined,
                    destinations: [],
                    specialties: splitCommaList(contactSpecialties),
                    languages: [],
                    notes: contactNotes.trim() || undefined,
                    category: "Contact",
                    category_group: "Contacts",
                    item_type: "CONTACT",
                    is_primary: false,
                    is_active: true,
                };
                const created = await contactsService.createContact(payload);
                setCreatedContactId(created.id);
                setStep("done");
            } catch (err) {
                console.error("Create contact failed:", err);
                const parsed = parseApiError(err, "Failed to save contact. Please try again.");
                setStep("error");
                setErrorMsg(parsed.message);
                setErrorInfo(parsed);
            }
            return;
        }

        // ── Client-side monthly deal quota gate ─────────────────────────────
        // Checked against the local codebase config (dealQuotaConfig.ts, mirroring
        // config.py) rather than the backend's live admin-config-backed usage
        // endpoint, so free-tier sellers get an immediate, config-driven block
        // instead of round-tripping to the server only to hit MONTHLY_QUOTA_EXCEEDED.
        const dealLimit = mode === "DEAL" ? getMonthlyDealLimit(myTier) : -1;
        if (dealLimit === 0) {
            setStep("error");
            setErrorMsg("Deal listings aren't available on your current plan. Upgrade to list deals.");
            setErrorInfo(null);
            return;
        }
        if (dealLimit > 0) {
            try {
                const inventory = await dealsService.listMyInventory(0, 200);
                const usedThisMonth = countDealsThisMonth(inventory.map((d) => d.created_at).filter(Boolean) as string[]);
                if (hasReachedMonthlyDealLimit(myTier, usedThisMonth)) {
                    setStep("error");
                    setErrorMsg(`You've reached your monthly deal listing limit (${dealLimit}/month) on your current plan. Upgrade to list more deals.`);
                    setErrorInfo(null);
                    return;
                }
            } catch {
                // If we can't determine current usage client-side, fall through and
                // let the backend's own quota enforcement be the final authority.
            }
        }

        setStep("uploading");
        setAnalysisJob(null);
        let dealId: string;

        try {
            const deal = await dealsService.submitDealListing(
                {
                    title: buildTitle().trim(),
                    description: buildDescription().trim(),
                },
                file ?? undefined,
            );
            dealId = deal.id;
            setCreatedDealId(dealId);
            setCreatedDealCollection(deal.collection);
            // Upload any pre-selected gallery images now that we have a deal ID
            if (galleryFiles.length) {
                void uploadGalleryToExistingDeal(dealId);
            }
        } catch (err) {
            console.error("Create deal failed:", err);
            const parsed = parseApiError(err, "Failed to create item. Please try again.");
            setStep("error");
            setErrorMsg(parsed.message);
            setErrorInfo(parsed);
            return;
        }

        const canRunAnalysis = file ? supportsFlyerAnalysis(mode, file) : true;
        if (!canRunAnalysis) {
            setAiResult({ ai_status: "skipped", ai_message: "Listing created. Add an OPENROUTER_API_KEY to enable AI enrichment." });
            setStep("done");
            return;
        }

        // Run AI analysis
        setStep("analyzing");
        finalizedDealRef.current = null; // arm finalize for this fresh analysis
        try {
            const job = await dealsService.analyzeDeal(dealId);
            setAnalysisJob(job);

            // The server tells us WHY it returned this job. If this exact file was
            // already analyzed, there's nothing to re-run — surface that clearly and
            // show the existing result instead of pretending to analyze again.
            if (job.already_analyzed) {
                clearAnalysisState();
                const extracted =
                    (job.result as AiResult | null) ??
                    { ai_status: "done", ai_message: job.message || "This deal is already analyzed." };
                setAiResult(extracted);
                setStep("done");
                return;
            }

            // Track this job so MyInventoryPage can show live status badges
            if (job.job_id) trackDealAnalysis(dealId, job.job_id, title);
            // Persist so that if user navigates away and returns, we restore the analyzing step
            saveAnalysisState({ dealId, dealTitle: title, jobId: job.job_id ?? "", startedAt: Date.now() });
            const finalJob =
                job.status === "completed" || job.status === "failed"
                    ? job
                    : await pollAnalysisJob(job.job_id);

            // Shared terminal transition (the WS handler may have already run it).
            await finalizeAnalysis(finalJob, dealId);
        } catch (err) {
            clearAnalysisState();
            console.error("AI analysis failed:", err);
            const parsed = parseApiError(err, "AI analysis failed. Your listing was saved but could not be enriched.");
            setErrorMsg(parsed.message);
            setErrorInfo(parsed);
            setStep("error");
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // RENDER: Home — unified upload-first entry + secondary modules
    // ─────────────────────────────────────────────────────────────────────
    if (step === "home") {
        return (
            <div className="page-shell">
                <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
                    <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
                        <button
                            onClick={() => navigate("/inventory/my")}
                            className="text-gray-500 hover:text-gray-800 text-sm font-medium"
                        >
                            ← My Inventory
                        </button>
                        <div className="h-5 w-px bg-gray-300" />
                        <h1 className="text-xl font-bold text-gray-900">New Listing</h1>
                    </div>
                </header>

                <main className="max-w-2xl mx-auto px-4 py-10">

                    {/* ── Primary: Travel Deal upload-first form ── */}
                    <div className="mb-10">
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Post a Travel Deal</h2>
                            <p className="text-gray-500 text-sm mt-2">
                                Upload a flyer or enter details — AI will structure everything for you.
                            </p>
                        </div>

                        {/* ── Stale-reset notice (previous stuck upload auto-reset) ── */}
                        {staleNotice && (
                            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2">
                                    <span aria-hidden className="text-amber-500 shrink-0">⚠️</span>
                                    <p className="text-xs text-amber-800">{staleNotice}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setStaleNotice(null)}
                                    className="text-amber-500 hover:text-amber-700 text-sm leading-none shrink-0"
                                    aria-label="Dismiss"
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        {/* ── Analysis in-progress block ── */}
                        {(() => {
                            const persisted = loadAnalysisState();
                            // Also catch jobs tracked in bgJobs but not in localStorage (e.g. another tab started analysis)
                            const bgActiveJob = [...bgJobs.values()].find(
                                (j) => j.kind === "deal_analysis" && j.status !== "completed" && j.status !== "failed"
                            );
                            if (!persisted && !bgActiveJob) return null;
                            const job = persisted ? bgJobs.get(`deal:${persisted.dealId}`) : bgActiveJob;
                            const isActive = !job || (job.status !== "completed" && job.status !== "failed");
                            if (!isActive) return null;
                            const dealTitle = persisted?.dealTitle || job?.title || "your deal";
                            const stageLabel: Record<string, string> = {
                                queued: "Queued for Gullivr AI review…",
                                ocr_running: "Reading your travel listing…", ocr_completed: "Listing read",
                                llm_running: "Structuring for B2B buyers…", llm_completed: "Deal structured",
                                index_running: "Preparing your Gullivr listing…", index_completed: "Draft ready on Gullivr",
                            };
                            const stage = job?.stage ?? "queued";
                            const pct = ({
                                queued: 5,
                                ocr_running: 25, ocr_completed: 35,
                                llm_running: 60, llm_completed: 72,
                                index_running: 85, index_completed: 95,
                            } as Record<string, number>)[stage] ?? 20;
                            return (
                                <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500 shrink-0" />
                                            <p className="text-sm font-semibold text-blue-800">Preparing "{dealTitle}" for Gullivr buyers</p>
                                        </div>
                                        <span className="text-xs text-blue-600 font-medium shrink-0">{stageLabel[stage] ?? "Structuring…"}</span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-blue-200 overflow-hidden mb-3">
                                        <div className="h-full rounded-full bg-blue-500 transition-all duration-700" style={{ width: `${pct}%` }} />
                                    </div>
                                    <p className="text-xs text-blue-700">Please wait for the current listing to finish publishing before uploading a new deal.</p>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/deals/create")}
                                        className="mt-3 px-4 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        View analysis progress →
                                    </button>
                                </div>
                            );
                        })()}

                        {(() => {
                            const isBlocked = Boolean(loadAnalysisState()) || [...bgJobs.values()].some(
                                (j) => j.kind === "deal_analysis" && j.status !== "completed" && j.status !== "failed"
                            );
                            return (
                        <form onSubmit={handleSubmit} className="space-y-6"
                            style={isBlocked ? { pointerEvents: "none", opacity: 0.45, userSelect: "none" } : undefined}
                        >

                            {/* ── File upload ── */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-800 mb-2">
                                    Flyer / Image / Video / PDF
                                    <span className="ml-2 text-xs font-normal text-gray-400">optional · images/PDFs up to {Number(import.meta.env.VITE_MAX_FILE_UPLOAD_MB ?? 50)} MB · videos up to {Number(import.meta.env.VITE_MAX_VIDEO_UPLOAD_MB ?? 100)} MB · PDF/DOC up to {Number(import.meta.env.VITE_MAX_DOC_PAGES ?? 8)} pages</span>
                                </label>

                                {file ? (
                                    <div className="rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
                                        {/* Preview area */}
                                        <div className="relative group">
                                            {preview ? (
                                                <img src={preview} alt="preview" className="w-full max-h-64 object-contain bg-gray-100" />
                                            ) : file.type.startsWith("video/") ? (
                                                <div className="flex flex-col items-center justify-center h-40 gap-2 bg-gray-900">
                                                    <span className="text-4xl">🎬</span>
                                                    <p className="text-xs text-gray-300">{file.name}</p>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center h-32 gap-3 bg-white">
                                                    <span className="text-4xl">{file.type === "application/pdf" ? "📑" : "📝"}</span>
                                                    <div>
                                                        <p className="font-semibold text-gray-800 text-sm">{file.name}</p>
                                                        <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {/* Action bar */}
                                        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-white">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-xs text-gray-500 truncate max-w-[160px]">{file.name}</span>
                                                <span className="text-[10px] text-gray-400">· {(file.size / 1024 / 1024).toFixed(1)} MB</span>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandFile({ file, previewUrl: preview })}
                                                    className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                                >
                                                    ⤢ Expand
                                                </button>
                                                <label className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
                                                    Change
                                                    <input type="file" accept={acceptedFiles(mode)} className="hidden" onChange={onFileChange} />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <label
                                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={onDrop}
                                        className={`flex flex-col items-center justify-center h-52 rounded-2xl border-2 border-dashed transition-colors cursor-pointer ${dragOver ? "border-blue-400 bg-blue-50" : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/40"}`}
                                    >
                                        <span className="text-4xl mb-3">🖼️</span>
                                        <p className="text-sm font-medium text-gray-700">Drag & drop your flyer here</p>
                                        <p className="text-xs text-gray-400 mt-1">or click to browse · image, PDF, DOC up to {Number(import.meta.env.VITE_MAX_FILE_UPLOAD_MB ?? 50)} MB · video up to {Number(import.meta.env.VITE_MAX_VIDEO_UPLOAD_MB ?? 100)} MB</p>
                                        <p className="text-xs text-gray-400">PDF/DOC: first {Number(import.meta.env.VITE_MAX_DOC_PAGES ?? 8)} pages only</p>
                                        <span className="mt-3 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg font-medium">Browse Flyers</span>
                                        <input type="file" accept={acceptedFiles(mode)} className="hidden" onChange={onFileChange} />
                                    </label>
                                )}
                            </div>

                            {/* ── Title ── */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-800 mb-1">
                                    Title <span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder='e.g. "Goa Beach Holiday 7N/8D from ₹12,999"'
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    maxLength={500}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
                                />
                                <p className={`mt-1 text-xs text-right ${title.length >= 500 ? "text-red-500" : title.length >= 450 ? "text-amber-500" : "text-gray-400"}`}>{title.length}/500</p>
                            </div>

                            {/* ── Description ── */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-800 mb-1">
                                    Description <span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <textarea
                                    rows={4}
                                    placeholder="Describe the deal — destination, duration, inclusions, pricing, booking terms…"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    maxLength={10000}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white resize-none"
                                />
                                <p className={`mt-1 text-xs text-right ${description.length >= 10000 ? "text-red-500" : description.length >= 9000 ? "text-amber-500" : "text-gray-400"}`}>{description.length}/10000</p>
                            </div>

                            {/* ── AI hint ── */}
                            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                <span className="text-xl mt-0.5">🤖</span>
                                <div>
                                    <p className="text-sm font-semibold text-blue-700">AI will enrich this for you</p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {file
                                            ? file.type.startsWith("video/")
                                                ? "AI will analyze video frames to extract category, destination, itinerary, inclusions, and pricing."
                                                : "Upload a flyer or document and let AI extract category, destination, itinerary, inclusions, and pricing."
                                            : "Add a file (image, PDF, DOC, video) to enable AI extraction — or skip and edit details manually after posting."}
                                    </p>
                                </div>
                            </div>

                            {/* ── Gallery: extra images/videos ── */}
                            {mode === "DEAL" && (
                                <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Gallery images <span className="normal-case font-normal text-gray-400">· optional · uploaded after posting · up to {MAX_GALLERY_FILES} images ({galleryFiles.length}/{MAX_GALLERY_FILES})</span>
                                    </p>

                                    {galleryPreviews.length > 0 && (
                                        <div>
                                            <div className="grid grid-cols-4 gap-2 mb-2">
                                                {galleryPreviews.map((src, gIdx) => (
                                                    <div key={gIdx} className="relative group">
                                                        {src ? (
                                                            <img src={src} alt={`gallery ${gIdx + 1}`} className="w-full h-16 object-cover rounded-lg border border-gray-200" />
                                                        ) : (
                                                            <div className="w-full h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 text-xs">
                                                                {galleryFiles[gIdx]?.name?.slice(0, 8) ?? "video"}
                                                            </div>
                                                        )}
                                                        {/* Expand overlay */}
                                                        <button
                                                            type="button"
                                                            onClick={() => setExpandFile({ file: galleryFiles[gIdx], previewUrl: src || null })}
                                                            className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all rounded-lg"
                                                        >
                                                            <span className="opacity-0 group-hover:opacity-100 text-white text-[10px] font-semibold bg-black/70 px-1.5 py-0.5 rounded transition-all">⤢</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeGalleryFile(gIdx)}
                                                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                                        >×</button>
                                                    </div>
                                                ))}
                                            </div>
                                            {galleryPreviews.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCarousel({ initialIndex: 0 })}
                                                    className="text-xs text-blue-600 font-medium hover:underline"
                                                >
                                                    View all {galleryPreviews.length} files →
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <label className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-gray-200 rounded-xl py-3 cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors">
                                        <input
                                            type="file"
                                            accept={GALLERY_ACCEPT_ATTR}
                                            multiple
                                            className="hidden"
                                            onChange={(e) => { onGalleryFilesSelected(e.target.files); e.target.value = ""; }}
                                        />
                                        <span className="text-sm text-gray-500">+ Add gallery images / documents</span>
                                        <span className="text-xs text-gray-400">({galleryFiles.length} selected)</span>
                                    </label>
                                </div>
                            )}

                            {errorMsg && (
                                errorInfo?.isSellerOnboardingIncomplete ? (
                                    <SellerOnboardingAlert
                                        message={errorInfo.message}
                                        missingFields={errorInfo.missingFields}
                                        missingFieldsDetailed={errorInfo.missingFieldsDetailed}
                                        returnTo={location.pathname}
                                    />
                                ) : (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{errorMsg}</div>
                                )
                            )}

                            <div className="pt-2 pb-8">
                                <button
                                    type="submit"
                                    className="w-full py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                                >
                                    Post Deal →
                                </button>
                            </div>
                        </form>
                            );
                        })()}
                    </div>

                    {/* ── Secondary modules ── */}
                    <div className="border-t border-gray-200 pt-8">
                        <p className="text-sm font-semibold text-gray-700 mb-4">Other modules</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

                            {/* AI Contact — coming soon */}
                            <div
                                aria-disabled="true"
                                className="relative flex flex-col items-start p-4 rounded-xl border-2 border-gray-200 bg-gray-50 text-left opacity-60 cursor-not-allowed"
                            >
                                <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">Coming soon</span>
                                <span className="text-2xl mb-2">👤</span>
                                <p className="font-bold text-sm text-gray-500">AI Contact Card</p>
                                <p className="text-xs text-gray-400 mt-1 leading-snug">Build your seller profile. AI structures your contact info for buyers.</p>
                            </div>

                            {/* AI Webinar */}
                            <button
                                onClick={() => navigate("/webinar")}
                                className="flex flex-col items-start p-4 rounded-xl border-2 border-rose-200 bg-rose-50 hover:border-rose-400 hover:shadow-sm transition-all text-left"
                            >
                                <span className="text-2xl mb-2">📹</span>
                                <p className="font-bold text-sm text-rose-700">AI Webinar</p>
                                <p className="text-xs text-gray-500 mt-1 leading-snug">Post a live event or webinar. Add your details, AI builds the listing.</p>
                            </button>

                            {/* AI Showcase — coming soon */}
                            <div
                                aria-disabled="true"
                                className="relative flex flex-col items-start p-4 rounded-xl border-2 border-gray-200 bg-gray-50 text-left opacity-60 cursor-not-allowed"
                            >
                                <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">Coming soon</span>
                                <span className="text-2xl mb-2">✨</span>
                                <p className="font-bold text-sm text-gray-500">AI Showcase</p>
                                <p className="text-xs text-gray-400 mt-1 leading-snug">Curate your best deals into a seller portfolio page.</p>
                            </div>

                            {/* Add News */}
                            <button
                                onClick={() => navigate("/news/add")}
                                className="flex flex-col items-start p-4 rounded-xl border-2 border-amber-200 bg-amber-50 hover:border-amber-400 hover:shadow-sm transition-all text-left"
                            >
                                <span className="text-2xl mb-2">📰</span>
                                <p className="font-bold text-sm text-amber-700">Add News</p>
                                <p className="text-xs text-gray-500 mt-1 leading-snug">Publish a news update or announcement. AI helps structure it for buyers.</p>
                            </button>

                        </div>
                    </div>

                    {/* ── File viewer / carousel dialogs (home step) ── */}
                    {expandFile && (
                        <FileViewerDialog
                            file={expandFile.file}
                            previewUrl={expandFile.previewUrl}
                            onClose={() => setExpandFile(null)}
                        />
                    )}
                    {showCarousel && galleryFiles.length > 0 && (
                        <FilesCarouselDialog
                            files={[
                                ...(file ? [{ file, previewUrl: preview, label: file.name }] : []),
                                ...galleryFiles.map((f, i) => ({ file: f, previewUrl: galleryPreviews[i] ?? null, label: f.name })),
                            ]}
                            initialIndex={showCarousel.initialIndex}
                            onClose={() => setShowCarousel(null)}
                        />
                    )}

                    {/* ── Secondary module forms ── */}
                    {mode !== "DEAL" && (
                        <div className="mt-8 border-t border-gray-200 pt-8">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="text-2xl">{MODE_ICONS[mode]}</span>
                                <div>
                                    <p className="font-bold text-gray-900">
                                        {mode === "AI_CONTACT" ? "Contact Card" : mode === "AI_WEBINAR" ? "AI Webinar" : "AI Showcase"}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {mode === "AI_CONTACT"
                                            ? "Create a seller contact card and specialty profile — saved to your business contacts."
                                            : null}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { setMode("DEAL"); setErrorMsg(""); setErrorInfo(null); }}
                                        className="text-xs text-gray-400 hover:text-gray-600 underline mt-1"
                                    >
                                        ← Back to Deal
                                    </button>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">

                                {/* AI_CONTACT → BusinessContactCreate */}
                                {mode === "AI_CONTACT" && (
                                    <div className="space-y-8">
                                        <section className="space-y-3">
                                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Person details</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Full name *</span>
                                                    <input
                                                        required
                                                        type="text"
                                                        placeholder="Rahul Sharma"
                                                        value={contactName}
                                                        onChange={(e) => setContactName(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Designation</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Regional Head – North India"
                                                        value={contactDesignation}
                                                        onChange={(e) => setContactDesignation(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Phone / WhatsApp</span>
                                                    <input
                                                        type="tel"
                                                        placeholder="+91 98765 43210"
                                                        value={contactPhone}
                                                        onChange={(e) => setContactPhone(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Email</span>
                                                    <input
                                                        type="email"
                                                        placeholder="rahul@company.com"
                                                        value={contactEmail}
                                                        onChange={(e) => setContactEmail(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                            </div>
                                        </section>

                                        <section className="space-y-3">
                                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Organization</p>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Vertical *</span>
                                                <select
                                                    required
                                                    value={contactVertical}
                                                    onChange={(e) => setContactVertical(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                >
                                                    <option value="">Select…</option>
                                                    {CONTACT_VERTICAL_OPTIONS.map((v) => (
                                                        <option key={v} value={v}>{v}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Company / organization *</span>
                                                <input
                                                    required
                                                    type="text"
                                                    placeholder="IndiGo"
                                                    value={contactCompany}
                                                    onChange={(e) => setContactCompany(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                />
                                            </label>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Company code</span>
                                                    <input
                                                        type="text"
                                                        placeholder="6E"
                                                        value={contactCompanyCode}
                                                        onChange={(e) => setContactCompanyCode(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Organization type *</span>
                                                    <select
                                                        required
                                                        value={contactOrgType}
                                                        onChange={(e) => setContactOrgType(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    >
                                                        <option value="">Select…</option>
                                                        {CONTACT_ORG_TYPE_OPTIONS.map((v) => (
                                                            <option key={v} value={v}>{v}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                            </div>
                                        </section>

                                        <section className="space-y-3">
                                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Department &amp; hierarchy</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Department</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Trade Sales"
                                                        value={contactDepartment}
                                                        onChange={(e) => setContactDepartment(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Sub-department</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Regional Sales"
                                                        value={contactSubDepartment}
                                                        onChange={(e) => setContactSubDepartment(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                            </div>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Hierarchy level</span>
                                                <select
                                                    value={contactHierarchyLevel}
                                                    onChange={(e) => setContactHierarchyLevel(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                >
                                                    <option value="">Select…</option>
                                                    {hierarchyLevelSelectOptions().map((opt) => {
                                                        const level = opt.split(":")[0]?.trim() ?? "";
                                                        return (
                                                            <option key={opt} value={level}>{opt}</option>
                                                        );
                                                    })}
                                                </select>
                                            </label>
                                        </section>

                                        <section className="space-y-3">
                                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Region &amp; coverage</p>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Region</span>
                                                <input
                                                    type="text"
                                                    placeholder="North India"
                                                    value={contactRegion}
                                                    onChange={(e) => setContactRegion(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Coverage areas (comma separated)</span>
                                                <input
                                                    type="text"
                                                    placeholder="Delhi NCR, Punjab, Haryana"
                                                    value={contactCoverage}
                                                    onChange={(e) => setContactCoverage(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                />
                                            </label>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">City</span>
                                                    <input
                                                        type="text"
                                                        placeholder="New Delhi"
                                                        value={contactCity}
                                                        onChange={(e) => setContactCity(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-800 mb-1">Country</span>
                                                    <input
                                                        type="text"
                                                        placeholder="India"
                                                        value={contactCountry}
                                                        onChange={(e) => setContactCountry(e.target.value)}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                    />
                                                </label>
                                            </div>
                                        </section>

                                        <section className="space-y-3">
                                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Specialties &amp; notes</p>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Specialties (comma separated)</span>
                                                <input
                                                    type="text"
                                                    placeholder="FIT, Group, MICE"
                                                    value={contactSpecialties}
                                                    onChange={(e) => setContactSpecialties(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Notes</span>
                                                <textarea
                                                    rows={2}
                                                    placeholder="Any additional info…"
                                                    value={contactNotes}
                                                    onChange={(e) => setContactNotes(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white resize-none"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Title *</span>
                                                <input
                                                    required
                                                    type="text"
                                                    placeholder="Short headline for your card"
                                                    value={title}
                                                    onChange={(e) => setTitle(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-800 mb-1">Description *</span>
                                                <textarea
                                                    required
                                                    rows={3}
                                                    placeholder="Seller-ready bio: what you offer, regions, and how buyers reach you."
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white resize-none"
                                                />
                                            </label>
                                        </section>

                                        <div className="flex gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                                            <span className="text-xl shrink-0" aria-hidden>✨</span>
                                            <div>
                                                <p className="text-sm font-semibold text-green-900">AI will extract the rest</p>
                                                <p className="text-xs text-green-800/90 mt-1 leading-snug">
                                                    Category, destinations, tags, and richer seller copy can be refined from your profile and listings over time.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* AI_WEBINAR fields */}
                                {mode === "AI_WEBINAR" && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-800 mb-1">Webinar Title <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                placeholder='e.g. "Kerala Backwaters — Full Guide for Agents"'
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-800 mb-1">Presenter Name</label>
                                                <input
                                                    type="text"
                                                    placeholder="Priya Verma"
                                                    value={presenter}
                                                    onChange={(e) => setPresenter(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-800 mb-1">Date</label>
                                                <input
                                                    type="date"
                                                    value={webinarDate}
                                                    onChange={(e) => setWebinarDate(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-800 mb-1">Time</label>
                                                <input
                                                    type="time"
                                                    value={webinarTime}
                                                    onChange={(e) => setWebinarTime(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-800 mb-1">Registration Link</label>
                                                <input
                                                    type="url"
                                                    placeholder="https://zoom.us/..."
                                                    value={webinarLink}
                                                    onChange={(e) => setWebinarLink(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-800 mb-1">Topics / Description</label>
                                            <textarea
                                                rows={2}
                                                placeholder="What the webinar covers, who should attend, key topics…"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white resize-none"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* AI_SHOWCASE fields */}
                                {mode === "AI_SHOWCASE" && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-800 mb-1">Showcase Title <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                placeholder='e.g. "Goa & Kerala Experts — Beach Holidays Specialists"'
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-800 mb-1">What you offer</label>
                                            <textarea
                                                rows={3}
                                                placeholder="e.g. We specialise in beach holidays, temple circuits, and group packages across India…"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white resize-none"
                                            />
                                        </div>
                                        <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                                            <span className="text-xl mt-0.5">💡</span>
                                            <p className="text-xs text-gray-600">
                                                After posting, link your best deals to this showcase from your inventory page.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {errorMsg && (
                                    errorInfo?.isSellerOnboardingIncomplete ? (
                                        <SellerOnboardingAlert
                                            message={errorInfo.message}
                                            missingFields={errorInfo.missingFields}
                                            missingFieldsDetailed={errorInfo.missingFieldsDetailed}
                                            returnTo={location.pathname}
                                        />
                                    ) : (
                                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{errorMsg}</div>
                                    )
                                )}

                                <div className="pt-2 pb-8">
                                    <button
                                        type="submit"
                                        className={
                                            mode === "AI_CONTACT"
                                                ? "w-full py-3.5 rounded-xl text-sm font-bold uppercase tracking-wide bg-[#f5f0e6] text-gray-900 border border-[#e8e0d4] hover:bg-[#ebe4d6] transition-colors shadow-sm"
                                                : "w-full py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                                        }
                                    >
                                        {mode === "AI_CONTACT"
                                            ? "Post contact card"
                                            : `Post ${mode === "AI_WEBINAR" ? "Webinar" : "Showcase"} →`}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </main>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // RENDER: Uploading / Analyzing — step-by-step progress stream
    // ─────────────────────────────────────────────────────────────────────────────
    if (step === "uploading" || step === "analyzing") {
        const isUploading = step === "uploading";

        // Map backend job stage → human-readable label
        // Mirrors backend/app/modules/deals/schemas/sse.py:DealAnalysisStage.
        // `_completed` stages are momentary transitions to the next phase, so
        // they share the label/icon of the phase they just finished.
        const STAGE_LABELS: Record<string, string> = {
            queued:          "Queued for Gullivr AI review",
            ocr_running:     "Reading your travel listing",
            ocr_completed:   "Listing read",
            llm_running:     "Structuring your deal for B2B buyers",
            llm_completed:   "Deal details structured",
            index_running:   "Preparing your Gullivr listing",
            index_completed: "Draft ready on Gullivr",
            completed:       "Saved as draft — publish to go live",
            failed:          "Analysis failed",
        };
        const STAGE_ICONS: Record<string, string> = {
            queued:          "⏳",
            ocr_running:     "🔍",
            ocr_completed:   "🔍",
            llm_running:     "🤖",
            llm_completed:   "🤖",
            index_running:   "📌",
            index_completed: "📌",
            completed:       "✅",
            failed:          "❌",
        };

        // Ordered pipeline steps shown as a stream
        const PIPELINE: Array<{ key: string; label: string; icon: string; desc: string }> = [
            { key: "upload",        icon: "☁️", label: "Uploading listing",        desc: "Saving your travel deal and flyer to Gullivr." },
            { key: "queued",        icon: "⏳", label: "Queued",                   desc: "Job created, Gullivr AI picking it up." },
            { key: "ocr_running",   icon: "🔍", label: "Reading your listing",     desc: "Scanning the flyer for deal details." },
            { key: "llm_running",   icon: "🤖", label: "Structuring for buyers",   desc: "Classifying category, destinations, pricing for B2B buyers…" },
            { key: "index_running", icon: "📌", label: "Publishing to Gullivr",    desc: "Making your deal discoverable to travel buyers." },
        ];

        // Determine the active pipeline step
        const currentStatus = analysisJob?.status;
        const currentStage  = analysisJob?.stage ?? currentStatus ?? "";

        // Upload step is "done" once we enter analyzing
        // Map backend status → pipeline key (stage field is most reliable).
        // `_completed` stages map to the SAME key as their paired `_running`
        // stage — they're transient markers, not new pipeline steps — so the
        // stepper never regresses when the backend emits them.
        const getActivePipelineKey = (): string => {
            if (isUploading) return "upload";
            const stageMap: Record<string, string> = {
                queued:          "queued",
                ocr_running:     "ocr_running",
                ocr_completed:   "ocr_running",
                llm_running:     "llm_running",
                llm_completed:   "llm_running",
                index_running:   "index_running",
                index_completed: "index_running",
                completed:       "index_running",
                failed:          "llm_running",
            };
            return stageMap[currentStage] ?? stageMap[currentStatus ?? ""] ?? "queued";
        };
        const activeKey = getActivePipelineKey();
        const activeIdx = PIPELINE.findIndex((s) => s.key === activeKey);

        // Nice label for the main heading
        const headingLabel = isUploading
            ? (mode === "AI_CONTACT" ? "Saving your contact card…" : "Uploading your listing to Gullivr…")
            : (STAGE_LABELS[currentStage] ?? STAGE_LABELS[currentStatus ?? ""] ?? "Gullivr AI is processing…");
        const headingIcon = isUploading ? "☁️" : (STAGE_ICONS[currentStage] ?? STAGE_ICONS[currentStatus ?? ""] ?? "🤖");

        // Detect if we're in a "reconnected" state (restored from sessionStorage on navigation back)
        const isReconnected = Boolean(loadAnalysisState()?.dealId && analysisElapsed === 0);

        // Server-authoritative timing (set by getAnalysisJob polling) — avoids the
        // client clock drifting from reality across navigations/backgrounded tabs.
        const serverElapsedSec = analysisJob?.elapsed_ms != null ? Math.round(analysisJob.elapsed_ms / 1000) : null;
        const estimatedTotalSec = analysisJob?.estimated_total_ms != null ? Math.round(analysisJob.estimated_total_ms / 1000) : null;

        return (
            <div className="page-shell flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full">

                    {/* Reconnected banner — shown when user navigated away and came back */}
                    {isReconnected && !isUploading && (
                        <div className="mb-5 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5">
                            <span className="text-blue-500 mt-0.5 shrink-0">🔁</span>
                            <div>
                                <p className="text-xs font-semibold text-blue-800">Still publishing to Gullivr</p>
                                <p className="text-xs text-blue-600 mt-0.5 leading-snug">
                                    Your deal was submitted and Gullivr AI is structuring it for B2B buyers in the background. You can leave this page — we'll notify you when it's live.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Spinner + icon */}
                    <div className="flex flex-col items-center mb-6">
                        <div className="relative w-16 h-16 mb-4">
                            <div className={`absolute inset-0 rounded-full border-4 ${currentStage === "completed" ? "border-green-200" : "border-blue-100"}`} />
                            {currentStage !== "completed" && (
                                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin" />
                            )}
                            {currentStage === "completed" && (
                                <div className="absolute inset-0 rounded-full border-4 border-green-500" />
                            )}
                            <div className="absolute inset-0 flex items-center justify-center text-2xl">{headingIcon}</div>
                        </div>
                        <h2 className="text-base font-bold text-gray-900 text-center">{headingLabel}</h2>
                        <p className="text-xs text-gray-400 mt-1 text-center">
                            {serverElapsedSec !== null
                                ? (estimatedTotalSec ? `${serverElapsedSec}s / ~${estimatedTotalSec}s elapsed` : `${serverElapsedSec}s elapsed`)
                                : analysisElapsed > 0 ? `${analysisElapsed}s elapsed` : "Resuming…"}
                        </p>
                    </div>

                    {/* Pipeline steps */}
                    <div className="space-y-0 relative">
                        {/* Vertical connector line */}
                        <div className="absolute left-[19px] top-4 bottom-4 w-px bg-gray-200 z-0" />

                        {PIPELINE.map((pipelineStep, idx) => {
                            const isDone   = idx < activeIdx;
                            const isActive = idx === activeIdx;
                            return (
                                <div key={pipelineStep.key} className="relative flex items-start gap-3 py-2.5 z-10">
                                    {/* Circle indicator */}
                                    <div className={`relative flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm border-2 transition-all duration-300 ${
                                        isDone    ? "bg-green-50 border-green-400 text-green-600"
                                        : isActive ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm ring-2 ring-blue-100"
                                        : "bg-white border-gray-200 text-gray-300"
                                    }`}>
                                        {isDone ? (
                                            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) : isActive ? (
                                            <span className="animate-pulse">{pipelineStep.icon}</span>
                                        ) : (
                                            <span className="opacity-30">{pipelineStep.icon}</span>
                                        )}
                                    </div>

                                    {/* Label + desc */}
                                    <div className="flex-1 min-w-0 pt-1.5">
                                        <p className={`text-sm font-semibold leading-tight ${isDone ? "text-green-700" : isActive ? "text-gray-900" : "text-gray-300"}`}>
                                            {pipelineStep.label}
                                            {isActive && (
                                                <span className="ml-2 inline-flex gap-0.5 align-middle">
                                                    {[0,1,2].map((i) => (
                                                        <span key={i} className="w-1 h-1 rounded-full bg-blue-500 animate-bounce inline-block" style={{ animationDelay: `${i * 0.15}s` }} />
                                                    ))}
                                                </span>
                                            )}
                                        </p>
                                        {(isDone || isActive) && (
                                            <p className={`text-xs mt-0.5 ${isDone ? "text-gray-400" : "text-gray-500"}`}>{pipelineStep.desc}</p>
                                        )}
                                    </div>

                                    {/* Duration badge for done */}
                                    {isDone && (
                                        <div className="flex-shrink-0 pt-1.5">
                                            <span className="text-[10px] text-green-600 font-medium bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">done</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer hint */}
                    <p className="text-[11px] text-gray-400 text-center mt-5 leading-snug">
                        {isUploading
                            ? "Almost there — upload usually takes a few seconds."
                            : estimatedTotalSec
                                ? `Gullivr AI usually takes about ${estimatedTotalSec}s to publish this type of upload. Please don't close this tab.`
                                : "Publishing to Gullivr takes 15–45 seconds. Please don't close this tab."}
                    </p>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // RENDER: Error
    // ─────────────────────────────────────────────────────────────────────────────
    if (step === "error") {
        // If deal was already created before the error (AI analysis failed), tell the user
        const dealAlreadySaved = !!createdDealId;
        return (
            <div className="page-shell flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-lg p-10 max-w-sm w-full text-center">
                    <div className="text-5xl mb-4">{dealAlreadySaved ? "⚠️" : "😕"}</div>
                    <h2 className="text-lg font-bold text-gray-900">
                        {dealAlreadySaved ? "Listing Saved — Analysis Failed" : "Something went wrong"}
                    </h2>
                    {dealAlreadySaved && (
                        <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-3 mb-1">
                            ✅ Your listing was saved successfully. The AI enrichment step failed — your deal is in your inventory but may need manual editing.
                        </p>
                    )}
                    {errorInfo?.isSellerOnboardingIncomplete ? (
                        <SellerOnboardingAlert
                            message={errorInfo.message}
                            missingFields={errorInfo.missingFields}
                            missingFieldsDetailed={errorInfo.missingFieldsDetailed}
                            returnTo={location.pathname}
                            className="mt-4 text-left"
                        />
                    ) : (
                        <>
                            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">{errorMsg}</p>
                            {errorMsg?.toLowerCase().includes("pending admin verification") && (
                                <button
                                    type="button"
                                    onClick={() => setShowVerificationModal(true)}
                                    className="text-xs text-indigo-600 hover:text-indigo-500 underline mt-1"
                                >
                                    Request Verification
                                </button>
                            )}
                        </>
                    )}
                    <div className="mt-6 flex flex-col gap-3">
                        {dealAlreadySaved ? (
                            <>
                                <button
                                    onClick={() => navigate("/inventory/my")}
                                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
                                >
                                    View My Inventory
                                </button>
                                <button
                                    onClick={() => setStep("home")}
                                    className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors"
                                >
                                    Create Another Listing
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setStep("home")}
                                className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
                            >
                                Try Again
                            </button>
                        )}
                    </div>
                </div>
                {showVerificationModal && (
                    <VerificationRequiredModal message={errorMsg || ""} onClose={() => setShowVerificationModal(false)} />
                )}
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // RENDER: Done
    // ─────────────────────────────────────────────────────────────────────────────
    if (createdContactId && mode === "AI_CONTACT") {
        return (
            <div className="page-shell flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="text-3xl">{MODE_ICONS.AI_CONTACT}</span>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Contact card saved</h2>
                        <p className="text-sm text-gray-500 mt-1 font-medium">{title.trim() || contactName}</p>
                    </div>
                    <div className="space-y-3 text-sm text-gray-700">
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Description</p>
                            <p className="whitespace-pre-line">{description.trim()}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{contactVertical}</span>
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{contactOrgType}</span>
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-800">{contactCompany}</span>
                        </div>
                        <p className="text-xs text-gray-400">Saved as business contact · ID {createdContactId}</p>
                    </div>
                    <div className="flex flex-col gap-3 mt-8">
                        <button
                            type="button"
                            onClick={() => navigate("/inventory/my")}
                            className="w-full py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            My Inventory
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setStep("home");
                                setMode("DEAL");
                                setTitle("");
                                setDescription("");
                                setFile(null);
                                setPreview(null);
                                setContactName("");
                                setContactDesignation("");
                                setContactPhone("");
                                setContactEmail("");
                                setContactVertical("");
                                setContactCompany("");
                                setContactCompanyCode("");
                                setContactOrgType("");
                                setContactDepartment("");
                                setContactSubDepartment("");
                                setContactHierarchyLevel("");
                                setContactRegion("");
                                setContactCoverage("");
                                setContactCity("");
                                setContactCountry("");
                                setContactSpecialties("");
                                setContactNotes("");
                                setWebinarDate("");
                                setWebinarTime("");
                                setWebinarLink("");
                                setPresenter("");
                                setCreatedDealId(null);
                                setCreatedDealCollection(null);
                                setCreatedContactId(null);
                                setAiResult(null);
                                setAnalysisJob(null);
                            }}
                            className="w-full py-3 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors shadow-sm"
                        >
                            + Post another
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const ai = aiResult || {};
    const hasExtracted = ai.ai_status === "done";
    const currentSavedTitle = buildTitle() || "Your listing";
    const rawExtractionJson = JSON.stringify(aiResult ?? {}, null, 2);

    const TagPill: React.FC<{ label: string; color: string }> = ({ label, color }) => (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>
    );

    const applyAiSuggestion = async (field: "title" | "description") => {
        if (!createdDealId || !createdDealCollection) return;
        const nextValue = field === "title" ? ai.ai_suggested_title : ai.ai_suggested_description;
        if (!nextValue) return;
        setIsApplyingAiField(field);
        try {
            const updated = await dealsService.updateInventoryItem(createdDealCollection, createdDealId, { [field]: nextValue });
            // Backend may have resolved a different collection (post-reclassification) — keep it for subsequent calls.
            if (updated?.collection && updated.collection !== createdDealCollection) {
                setCreatedDealCollection(updated.collection);
            }
            if (field === "title") {
                setTitle(nextValue);
                setAiResult((current) => current ? { ...current, ai_used_suggested_title: true } : current);
            } else {
                setDescription(nextValue);
                setAiResult((current) => current ? { ...current, ai_used_suggested_description: true } : current);
            }
        } catch (err) {
            const parsed = parseApiError(err, `Failed to apply AI ${field}.`);
            setErrorMsg(parsed.message);
            setErrorInfo(parsed);
            setStep("error");
        } finally {
            setIsApplyingAiField(null);
        }
    };

    // ── Seller type mismatch: deal was deleted, show profile update prompt ──────
    if (ai.ai_status === "seller_type_mismatch") {
        const detectedGroup = ai.detected_group;
        const GROUP_LABELS: Record<string, string> = {
            stays: "Stays / Hotels", packages: "Packages", flights: "Flights",
            cruises: "Cruises", transfers: "Transfers / Transport",
            experiences: "Experiences", services: "Services", media: "Media",
        };
        const detectedLabel = detectedGroup ? (GROUP_LABELS[detectedGroup] ?? detectedGroup) : null;
        return (
            <div className="page-shell flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="text-3xl">🚫</span>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Deal Not Published</h2>
                        <p className="text-sm text-gray-500 mt-1">Your seller profile doesn't allow this deal type</p>
                    </div>

                    {detectedLabel && (
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                            <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">AI Detected Category</p>
                            <p className="text-sm font-bold text-blue-900">{detectedLabel}</p>
                        </div>
                    )}

                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 leading-relaxed mb-5">
                        {ai.ai_message || "Your seller profile does not allow publishing this category of deal."}
                    </div>

                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 mb-6">
                        <p className="font-semibold mb-1">To fix this:</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs text-gray-600">
                            <li>Go to your profile and update your <strong>Seller Types</strong></li>
                            {detectedLabel && <li>Add <strong>{detectedLabel}</strong> as one of your seller types</li>}
                            <li>Come back and create this deal again</li>
                        </ol>
                    </div>

                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => navigate("/profile")}
                            className="w-full py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                        >
                            Update My Seller Profile →
                        </button>
                        <button
                            onClick={() => { setStep("home"); setAiResult(null); setTitle(""); setDescription(""); setFile(null); setPreview(null); }}
                            className="w-full py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Try a Different Deal
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell flex items-center justify-center p-6">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full">

                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-3xl">{MODE_ICONS[mode] ?? "✅"}</span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">
                        {mode === "AI_CONTACT" ? "Contact Card Saved" : mode === "AI_WEBINAR" ? "Webinar Saved" : mode === "AI_SHOWCASE" ? "Showcase Saved" : "Deal Added to Your Inventory"}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1 font-medium">{currentSavedTitle}</p>
                    {mode !== "AI_CONTACT" && mode !== "AI_WEBINAR" && mode !== "AI_SHOWCASE" && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 text-left">
                            📥 Saved as <strong>inactive</strong> in your inventory. Go to <strong>My Inventory</strong> and make it live to publish it on the marketplace.
                        </p>
                    )}
                </div>

                {hasExtracted ? (
                    <div className="space-y-5">
                        {/* ── Title field with AI apply ── */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={500}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-800 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                            {ai.ai_suggested_title && !ai.ai_used_suggested_title && createdDealId && (
                                <div className="mt-2 flex items-start gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                                    <span className="text-blue-500 mt-0.5 text-xs">🤖</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-blue-600 font-semibold mb-0.5">AI suggested title</p>
                                        <p className="text-xs text-gray-700 leading-snug">{ai.ai_suggested_title}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => void applyAiSuggestion("title")}
                                        disabled={isApplyingAiField !== null}
                                        className="flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isApplyingAiField === "title" ? "Applying…" : "Apply"}
                                    </button>
                                </div>
                            )}
                            {ai.ai_used_suggested_title && (
                                <p className="mt-1 text-xs text-emerald-600 font-medium">✓ AI title applied</p>
                            )}
                        </div>

                        {/* ── Description field with AI apply ── */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</label>
                            <textarea
                                rows={4}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={5000}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-800 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                            />
                            {ai.ai_suggested_description && !ai.ai_used_suggested_description && createdDealId && (
                                <div className="mt-2 flex items-start gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                                    <span className="text-blue-500 mt-0.5 text-xs">🤖</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-blue-600 font-semibold mb-0.5">AI suggested description</p>
                                        <p className="text-xs text-gray-700 leading-snug line-clamp-3">{ai.ai_suggested_description}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => void applyAiSuggestion("description")}
                                        disabled={isApplyingAiField !== null}
                                        className="flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isApplyingAiField === "description" ? "Applying…" : "Apply"}
                                    </button>
                                </div>
                            )}
                            {ai.ai_used_suggested_description && (
                                <p className="mt-1 text-xs text-emerald-600 font-medium">✓ AI description applied</p>
                            )}
                        </div>

                        {/* ── AI extraction summary ── */}
                        <div className="border-t border-gray-100 pt-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                <span>🤖</span> AI extracted details
                            </p>
                        {ai.category && (
                            <div className="mb-3">
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Category</p>
                                <TagPill label={ai.category} color="bg-indigo-100 text-indigo-700" />
                            </div>
                        )}
                        {ai.categories?.primary_slug && (
                            <div className="mb-3">
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">AI Category Slugs</p>
                                <div className="flex flex-wrap gap-1.5">
                                    <TagPill label={`slug: ${ai.categories.primary_slug}`} color="bg-indigo-100 text-indigo-700" />
                                    {(ai.categories.secondary_slugs ?? []).map((s) => (
                                        <TagPill key={s} label={s} color="bg-purple-100 text-purple-700" />
                                    ))}
                                </div>
                            </div>
                        )}
                        {ai.item_type && ai.item_type !== mode && (
                            <div className="mb-3">
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Type</p>
                                <TagPill label={ai.item_type} color="bg-amber-100 text-amber-700" />
                            </div>
                        )}
                        {ai.destinations && ai.destinations.length > 0 && (
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Destinations</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {ai.destinations.map((d, i) => (
                                        <TagPill key={i} label={`📍 ${d}`} color="bg-blue-100 text-blue-700" />
                                    ))}
                                </div>
                            </div>
                        )}
                        {ai.duration && (
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Duration</p>
                                <TagPill label={`🕐 ${ai.duration}`} color="bg-amber-100 text-amber-700" />
                            </div>
                        )}
                        {ai.tags && ai.tags.length > 0 && (
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Tags</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {ai.tags.map((t, i) => (
                                        <TagPill key={i} label={`# ${t}`} color="bg-gray-100 text-gray-600" />
                                    ))}
                                </div>
                            </div>
                        )}
                        {ai.inclusions && ai.inclusions.length > 0 && (
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Inclusions</p>
                                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                                    {ai.inclusions.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {ai.exclusions && ai.exclusions.length > 0 && (
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Exclusions</p>
                                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                                    {ai.exclusions.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {ai.highlights && ai.highlights.length > 0 && (
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Highlights</p>
                                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                                    {ai.highlights.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {(ai.original_price != null || ai.discounted_price != null) && (
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Pricing</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {ai.original_price != null && (
                                        <TagPill label={`Original ${ai.original_price}`} color="bg-gray-100 text-gray-700" />
                                    )}
                                    {ai.discounted_price != null && (
                                        <TagPill label={`Deal ${ai.discounted_price}`} color="bg-emerald-100 text-emerald-700" />
                                    )}
                                </div>
                            </div>
                        )}
                        {ai.star_rating != null && (
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Star Rating</p>
                                <TagPill label={"★".repeat(Math.max(0, Math.min(5, ai.star_rating)))} color="bg-yellow-100 text-yellow-700" />
                            </div>
                        )}
                        </div>{/* end AI extraction summary border-t div */}

                        {/* ── Slug editor ── */}
                        <div className="border-t border-gray-100 pt-4 space-y-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                                <span>🔗</span> Category Slugs
                            </p>

                            {/* Deal type (group_slug / typed collection) */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Deal Type <span className="font-normal text-gray-400">(defines the typed collection &amp; Pinecone namespace)</span>
                                </label>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={primaryGroupSlug}
                                        disabled={!!createdDealCollection}
                                        onChange={(e) => { if (!createdDealCollection) { setPrimaryGroupSlug(e.target.value); setSlugSaved(false); } }}
                                        className="px-3 py-2 border border-indigo-300 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                        title={createdDealCollection ? "Deal type is locked — deal is already promoted to a typed collection" : undefined}
                                    >
                                        <option value="">— pick type —</option>
                                        {DEAL_TYPE_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                    <span className="text-gray-400 text-xs font-mono">/</span>
                                    {categoryLoadError ? (
                                        <p className="flex-1 px-3 py-2 border border-red-300 bg-red-50 rounded-xl text-xs text-red-600 font-medium">{categoryLoadError}</p>
                                    ) : (
                                        <select
                                            value={primarySlug}
                                            onChange={(e) => { if (!createdDealCollection) { setPrimarySlug(e.target.value); setSlugSaved(false); } }}
                                            disabled={categoryOptions.length === 0 || !!createdDealCollection}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                            title={createdDealCollection ? "Category slug is locked — deal is already promoted to a typed collection" : undefined}
                                        >
                                            <option value="">{categoryOptions.length === 0 ? "Loading…" : "— pick primary slug —"}</option>
                                            {categoryOptions
                                                .filter((c) => !c.is_promotional && (!primaryGroupSlug || c.group_slug === primaryGroupSlug))
                                                .map((c) => (
                                                    <option key={c.slug} value={c.slug}>{c.name} ({c.slug})</option>
                                                ))}
                                        </select>
                                    )}
                                </div>
                                {(() => {
                                    const aiCats = ai.categories as Record<string, unknown> | undefined;
                                    const aiGroup = aiCats?.group_slug as string | undefined;
                                    const aiSlug  = aiCats?.primary_slug as string | undefined;
                                    const groupDiff = aiGroup && aiGroup !== primaryGroupSlug;
                                    const slugDiff  = aiSlug && aiSlug !== primarySlug;
                                    if (!groupDiff && !slugDiff) return null;
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (aiGroup) setPrimaryGroupSlug(aiGroup);
                                                if (aiSlug)  setPrimarySlug(aiSlug);
                                                setSlugSaved(false);
                                            }}
                                            className="mt-1 text-xs text-indigo-600 hover:underline"
                                        >
                                            ↩ Restore AI: {aiGroup}/{aiSlug}
                                        </button>
                                    );
                                })()}
                                {createdDealCollection ? (
                                    <p className="mt-1 text-[11px] text-gray-500 font-medium">
                                        🔒 Deal type is locked — this deal is already promoted to the <strong>{createdDealCollection}</strong> collection.
                                    </p>
                                ) : primaryGroupSlug && (aiResult?.categories as Record<string,unknown> | undefined)?.group_slug !== primaryGroupSlug && (
                                    <p className="mt-1 text-[11px] text-amber-600 font-medium">
                                        ⚠ Changing deal type will reroute this deal to the <strong>{primaryGroupSlug}</strong> collection and Pinecone namespace on save.
                                    </p>
                                )}
                            </div>

                            {/* Secondary slugs */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Secondary Slugs <span className="font-normal text-gray-400">(multiple · cross-category routing)</span>
                                </label>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {secondarySlugs.map((slug, si) => (
                                        <span key={si} className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                                            {slug}
                                            <button
                                                type="button"
                                                onClick={() => { setSecondarySlugs((prev) => prev.filter((_, i) => i !== si)); setSlugSaved(false); }}
                                                className="ml-0.5 text-purple-500 hover:text-purple-800 font-bold leading-none"
                                            >×</button>
                                        </span>
                                    ))}
                                    {secondarySlugs.length === 0 && <span className="text-xs text-gray-400 italic">No secondary slugs yet</span>}
                                </div>
                                <div className="flex gap-2">
                                    {categoryLoadError ? (
                                        <p className="flex-1 px-3 py-2 border border-red-300 bg-red-50 rounded-xl text-xs text-red-600 font-medium">{categoryLoadError}</p>
                                    ) : (
                                        <select
                                            value=""
                                            disabled={categoryOptions.length === 0}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val && !secondarySlugs.includes(val)) {
                                                    setSecondarySlugs((prev) => [...prev, val]);
                                                    setSlugSaved(false);
                                                }
                                            }}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none bg-white disabled:opacity-50"
                                        >
                                            <option value="">{categoryOptions.length === 0 ? "Loading…" : "— add secondary slug —"}</option>
                                            {categoryOptions
                                                .filter((c) => !secondarySlugs.includes(c.slug) && c.slug !== primarySlug)
                                                .map((c) => (
                                                    <option key={c.slug} value={c.slug}>{c.name} ({c.slug}){c.is_promotional ? " ★" : ""}</option>
                                                ))}
                                        </select>
                                    )}
                                </div>
                                {ai.categories?.secondary_slugs && ai.categories.secondary_slugs.length > 0 && (
                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">AI suggested:</span>
                                        {ai.categories.secondary_slugs.map((s) => (
                                            <button
                                                key={s}
                                                type="button"
                                                onClick={() => {
                                                    if (!secondarySlugs.includes(s)) {
                                                        setSecondarySlugs((prev) => [...prev, s]);
                                                        setSlugSaved(false);
                                                    }
                                                }}
                                                className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${secondarySlugs.includes(s) ? "bg-purple-100 border-purple-300 text-purple-600" : "bg-white border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600"}`}
                                            >
                                                {secondarySlugs.includes(s) ? "✓ " : "+ "}{s}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Save slugs button */}
                            <button
                                type="button"
                                onClick={() => void saveSlugs()}
                                disabled={isSavingSlugs || !createdDealId}
                                className="w-full py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                            >
                                {isSavingSlugs ? "Saving slugs…" : slugSaved ? "✓ Slugs saved" : "Save Slugs"}
                            </button>
                        </div>

                        {/* ── Deal Package Toggle + Package Slugs ── */}
                        <div className="border-t border-gray-100 pt-4 space-y-4">
                            {primaryGroupSlug === "packages" ? (
                                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-indigo-50 border border-indigo-200">
                                    <span className="text-indigo-500 text-sm">📦</span>
                                    <p className="text-xs font-semibold text-indigo-700">
                                        This deal is already in the Packages collection — packages namespace indexing is applied automatically.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-gray-800">Also publish as Package</p>
                                            <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                                                Your deal will also appear in the Packages feed and be dual-indexed in the packages Pinecone namespace.
                                            </p>
                                            {packageToggleSaved && (
                                                <p className="text-xs text-emerald-600 font-medium mt-1">
                                                    ✓ {isInPackages ? "Added to Packages" : "Removed from Packages"}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={isInPackages}
                                            disabled={isTogglingPackages || !createdDealId}
                                            onClick={() => void togglePackages(!isInPackages)}
                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 mt-0.5 ${isInPackages ? "bg-indigo-600" : "bg-gray-300"}`}
                                        >
                                            <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-0 transition-transform duration-200 ease-in-out ${isInPackages ? "translate-x-5" : "translate-x-0"}`} />
                                        </button>
                                    </div>

                                    {/* Package-specific slugs — visible only when toggle is on */}
                                    {isInPackages && (
                                        <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 space-y-3">
                                            <p className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wide">Package Slug Settings</p>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                    Package Primary Slug <span className="font-normal text-gray-400">(how it appears in the packages namespace)</span>
                                                </label>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="px-2 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-lg shrink-0">packages/</span>
                                                    {categoryLoadError ? (
                                                        <p className="flex-1 px-3 py-2 border border-red-300 bg-red-50 rounded-xl text-xs text-red-600 font-medium">{categoryLoadError}</p>
                                                    ) : (
                                                        <select
                                                            value={pkgPrimarySlug}
                                                            onChange={(e) => { setPkgPrimarySlug(e.target.value); setSlugSaved(false); }}
                                                            disabled={categoryOptions.length === 0}
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none bg-white disabled:opacity-50"
                                                        >
                                                            <option value="">{categoryOptions.length === 0 ? "Loading…" : "— pick package primary slug —"}</option>
                                                            {categoryOptions
                                                                .filter((c) => !c.is_promotional && c.group_slug === "packages")
                                                                .map((c) => (
                                                                    <option key={c.slug} value={c.slug}>{c.name} ({c.slug})</option>
                                                                ))}
                                                        </select>
                                                    )}
                                                </div>
                                                {primarySlug && primarySlug !== pkgPrimarySlug && (
                                                    <button type="button" onClick={() => { setPkgPrimarySlug(primarySlug); setSlugSaved(false); }} className="mt-1 text-xs text-indigo-600 hover:underline">
                                                        ↩ Use main slug: {primarySlug}
                                                    </button>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                                    Package Secondary Slugs <span className="font-normal text-gray-400">(cross-category routing in packages)</span>
                                                </label>
                                                <div className="flex flex-wrap gap-1.5 mb-2">
                                                    {pkgSecondarySlugs.map((slug, si) => (
                                                        <span key={si} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                                                            {slug}
                                                            <button type="button" onClick={() => { setPkgSecondarySlugs((p) => p.filter((_, i) => i !== si)); setSlugSaved(false); }} className="ml-0.5 text-indigo-500 hover:text-indigo-800 font-bold leading-none">×</button>
                                                        </span>
                                                    ))}
                                                    {pkgSecondarySlugs.length === 0 && <span className="text-xs text-gray-400 italic">No package secondary slugs yet</span>}
                                                </div>
                                                <div className="flex gap-2">
                                                    {categoryLoadError ? (
                                                        <p className="flex-1 px-3 py-2 border border-red-300 bg-red-50 rounded-xl text-xs text-red-600 font-medium">{categoryLoadError}</p>
                                                    ) : (
                                                        <select
                                                            value=""
                                                            disabled={categoryOptions.length === 0}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                if (val && !pkgSecondarySlugs.includes(val)) {
                                                                    setPkgSecondarySlugs((prev) => [...prev, val]);
                                                                    setSlugSaved(false);
                                                                }
                                                            }}
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none bg-white disabled:opacity-50"
                                                        >
                                                            <option value="">{categoryOptions.length === 0 ? "Loading…" : "— add package secondary slug —"}</option>
                                                            {categoryOptions
                                                                .filter((c) => c.group_slug === "packages" && !pkgSecondarySlugs.includes(c.slug) && c.slug !== pkgPrimarySlug)
                                                                .map((c) => (
                                                                    <option key={c.slug} value={c.slug}>{c.name} ({c.slug}){c.is_promotional ? " ★" : ""}</option>
                                                                ))}
                                                        </select>
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-gray-400 mt-1">Saved together with main slugs via Save Slugs button.</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* ── Token usage summary ── */}
                        {ai.ai_token_usage && (ai.ai_token_usage.total_tokens ?? 0) > 0 && (
                            <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">AI Token Usage</p>
                                <div className="flex flex-wrap gap-3">
                                    <div className="text-center">
                                        <p className="text-base font-bold text-gray-800">{(ai.ai_token_usage.total_tokens ?? 0).toLocaleString()}</p>
                                        <p className="text-[10px] text-gray-400">Total</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-base font-bold text-blue-700">{(ai.ai_token_usage.prompt_tokens ?? 0).toLocaleString()}</p>
                                        <p className="text-[10px] text-gray-400">Prompt</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-base font-bold text-green-700">{(ai.ai_token_usage.completion_tokens ?? 0).toLocaleString()}</p>
                                        <p className="text-[10px] text-gray-400">Completion</p>
                                    </div>
                                    {ai.ai_elapsed_ms && ai.ai_elapsed_ms > 0 && (
                                        <div className="text-center">
                                            <p className="text-base font-bold text-gray-600">{(ai.ai_elapsed_ms / 1000).toFixed(1)}s</p>
                                            <p className="text-[10px] text-gray-400">Duration</p>
                                        </div>
                                    )}
                                </div>
                                {ai.ai_model && (
                                    <p className="text-[10px] text-gray-400 mt-2 truncate">Model: {ai.ai_model}</p>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 text-center">
                        {ai.ai_status === "skipped"
                            ? ai.ai_message || "Add an OPENROUTER_API_KEY to enable AI enrichment."
                            : "AI could not extract details. You can add them manually via Edit."}
                    </div>
                )}

                <div className="flex flex-col gap-3 mt-8">
                    {isDevUser && aiResult && (
                        <button
                            type="button"
                            onClick={() => setShowRawExtractionDialog(true)}
                            className="w-full py-3 border border-purple-300 bg-purple-50 text-purple-700 rounded-xl text-sm font-semibold hover:bg-purple-100 transition-colors"
                        >
                            🔍 View Raw AI Extraction (Dev)
                        </button>
                    )}
                    {/* ── Add gallery images after posting ── */}
                    {createdDealId && (
                        <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add Gallery Images</p>

                            {galleryUploadedKeys.length > 0 && (
                                <p className="text-xs text-emerald-600 font-medium">
                                    ✓ {galleryUploadedKeys.length} image{galleryUploadedKeys.length !== 1 ? "s" : ""} added to gallery
                                </p>
                            )}

                            {galleryPreviews.length > 0 && (
                                <div className="grid grid-cols-4 gap-2">
                                    {galleryPreviews.map((src, idx) => (
                                        <div key={idx} className="relative group">
                                            {src ? (
                                                <img src={src} alt={`gallery ${idx + 1}`} className="w-full h-16 object-cover rounded-lg border border-gray-200" />
                                            ) : (
                                                <div className="w-full h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 text-xs">
                                                    {galleryFiles[idx]?.name?.slice(0, 8) ?? "video"}
                                                </div>
                                            )}
                                            {!galleryUploading && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeGalleryFile(idx)}
                                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                >×</button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <label className={`flex items-center justify-center gap-2 w-full border-2 border-dashed rounded-xl py-3 transition-colors ${galleryUploading ? "border-blue-200 bg-blue-50 cursor-wait" : "border-gray-200 cursor-pointer hover:border-blue-300 hover:bg-blue-50"}`}>
                                <input
                                    type="file"
                                    accept={GALLERY_ACCEPT_ATTR}
                                    multiple
                                    className="hidden"
                                    disabled={galleryUploading}
                                    onChange={(e) => { onGalleryFilesSelected(e.target.files); e.target.value = ""; }}
                                />
                                <span className="text-sm text-gray-500">
                                    {galleryUploading ? galleryProgress ?? "Uploading…" : `+ Add images / documents${galleryFiles.length ? ` (${galleryFiles.length} staged)` : ""}`}
                                </span>
                            </label>

                            {galleryFiles.length > 0 && !galleryUploading && (
                                <button
                                    type="button"
                                    onClick={() => void uploadGalleryToExistingDeal(createdDealId)}
                                    className="w-full py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors"
                                >
                                    Upload {galleryFiles.length} file{galleryFiles.length !== 1 ? "s" : ""} to Gallery →
                                </button>
                            )}
                        </div>
                    )}

                    {createdDealId && (
                        <button
                            onClick={() =>
                                navigate("/inventory/my", { state: { editItemId: createdDealId } })
                            }
                            className="w-full py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                        >
                            ✏️ Edit Details
                        </button>
                    )}
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate("/inventory/my")}
                            className="flex-1 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            My Inventory
                        </button>
                        <button
                            onClick={() => {
                                setStep("home");
                                setMode("DEAL");
                                setTitle("");
                                setDescription("");
                                setFile(null);
                                setPreview(null);
                                setContactName("");
                                setContactDesignation("");
                                setContactPhone("");
                                setContactEmail("");
                                setContactVertical("");
                                setContactCompany("");
                                setContactCompanyCode("");
                                setContactOrgType("");
                                setContactDepartment("");
                                setContactSubDepartment("");
                                setContactHierarchyLevel("");
                                setContactRegion("");
                                setContactCoverage("");
                                setContactCity("");
                                setContactCountry("");
                                setContactSpecialties("");
                                setContactNotes("");
                                setWebinarDate("");
                                setWebinarTime("");
                                setWebinarLink("");
                                setPresenter("");
                                setCreatedDealId(null);
                                setCreatedDealCollection(null);
                                setCreatedContactId(null);
                                setAiResult(null);
                                setAnalysisJob(null);
                            }}
                            className="flex-1 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            + Add Another
                        </button>
                    </div>
                </div>
            </div>
            {/* ── File expand dialog ── */}
            {expandFile && (
                <FileViewerDialog
                    file={expandFile.file}
                    previewUrl={expandFile.previewUrl}
                    onClose={() => setExpandFile(null)}
                />
            )}
            {/* ── Carousel dialog ── */}
            {showCarousel && galleryFiles.length > 0 && (
                <FilesCarouselDialog
                    files={galleryFiles.map((f, i) => ({ file: f, previewUrl: galleryPreviews[i] ?? null, label: f.name }))}
                    initialIndex={showCarousel.initialIndex}
                    onClose={() => setShowCarousel(null)}
                />
            )}
            {isDevUser && showRawExtractionDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-base font-bold text-gray-900">Raw AI Extraction Payload (Development)</h3>
                            <button
                                type="button"
                                onClick={() => setShowRawExtractionDialog(false)}
                                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                        <div className="p-4 max-h-[70vh] overflow-auto bg-gray-950">
                            <pre className="text-xs leading-5 text-green-200 whitespace-pre-wrap break-words">
                                {rawExtractionJson}
                            </pre>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateDealPage;
